<template>
  <div>
    <div
      class="page-reservation"
      :class="{ 'is-embedded': embedded }"
      :style="$isMobile ? { textAlign: 'left' } : {}"
    >
      <ConfirmDialog></ConfirmDialog>

      <div v-if="isRequestStatus" class="request-status-banner">
        <div class="rsb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </div>
        <div class="rsb-content">
          <strong>{{ $t("reservasHc.pageReservation.requestPendingTitle") }}</strong>
          <span>{{ $t("reservasHc.pageReservation.requestPendingText") }}</span>
        </div>
        <div class="rsb-actions">
          <button class="rsb-btn rsb-btn--approve" :disabled="approvingRequest" @click="approveRequest">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {{ approvingRequest ? $t("reservasHc.pageReservation.approving") : $t("reservasHc.pageReservation.approve") }}
          </button>
          <button class="rsb-btn rsb-btn--reject" :disabled="rejectingRequest" @click="confirmRejectRequest">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            {{ rejectingRequest ? $t("reservasHc.pageReservation.rejecting") : $t("reservasHc.pageReservation.reject") }}
          </button>
        </div>
      </div>

      <div v-if="isRejectedRequestStatus" class="rejected-status-banner">
        <div class="rsb-icon rsb-icon--rejected">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <div class="rsb-content rsb-content--rejected">
          <strong>{{ $t("reservasHc.pageReservation.requestRejectedTitle") }}</strong>
          <span>{{ $t("reservasHc.pageReservation.requestRejectedText") }}</span>
        </div>
      </div>

      <div v-if="isIntentStatus" class="intent-status-banner">
        <div class="isb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div class="isb-content">
          <strong>{{ $t("reservasHc.pageReservation.awaitingPaymentTitle") }}</strong>
          <span>{{ $t("reservasHc.pageReservation.awaitingPaymentText") }}</span>
        </div>
        <div class="isb-side">
          <div v-if="intentCountdown" class="isb-countdown">
            <span class="isb-time">{{ intentCountdown }}</span>
            <small>{{ $t("reservasHc.pageReservation.remaining") }}</small>
          </div>

          <div class="isb-actions">
            <button
              v-if="canMarkIntentAsPaid && !ocultaImportes"
              class="isb-paid-btn"
              :disabled="markingAsPaid || markingAsExpired"
              @click="openMarkPaidModal"
            >
              {{ markingAsPaid ? $t("reservasHc.pageReservation.marking") : $t("reservasHc.pageReservation.markAsPaid") }}
            </button>

            <button
              v-if="canMarkIntentAsExpired"
              class="isb-expire-btn"
              :disabled="markingAsExpired || markingAsPaid"
              @click="markIntentAsExpired"
            >
              {{ markingAsExpired ? $t("reservasHc.pageReservation.expiring") : $t("reservasHc.pageReservation.markAsExpired") }}
            </button>
          </div>
        </div>
      </div>

      <transition name="exb">
      <div v-if="hasPendingExtension" class="ext-banner" :class="'ext-banner--' + reservationData.extension_status">
        <div class="exb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="17" rx="2.5"/><path d="M3 9h18M8 2v4M16 2v4M12 13v4M10 15h4"/></svg>
        </div>
        <div class="exb-content">
          <strong>
            {{ $t("reservasHc.pageReservation.extensionPendingTitle") }}
            <span v-if="reservationData.extension_code" class="exb-code">{{ reservationData.extension_code }}</span>
          </strong>
          <span>
            {{ $t("reservasHc.pageReservation.extraNights", { count: reservationData.extension_nights || 1 }) }} · {{ $t("reservasHc.pageReservation.newCheckout") }} {{ formatExtDate(reservationData.fecha_salida) }}<template v-if="extensionPriceLabel"> · <b>{{ extensionPriceLabel }}</b></template>
            ·
            <template v-if="reservationData.extension_status === 'intent'">{{ $t("reservasHc.pageReservation.awaitingGuestPayment") }}</template>
            <template v-else>{{ $t("reservasHc.pageReservation.pendingApproval") }}</template>
            <!-- Cuánto le queda al huésped. Al agente le decimos «tienes 20 minutos» y
                 aquí ponía solo «esperando el pago»: el anfitrión no sabía si quedaban
                 19 minutos o 10 segundos, y la tarjeta cambiaba de golpe al vencer. -->
            <template v-if="cuentaAtrasAmpliacion">
              · <b class="exb-plazo" :class="{ 'exb-plazo--poco': plazoApurado }">{{ cuentaAtrasAmpliacion }}</b>
            </template>
          </span>
        </div>
        <div class="exb-actions">
          <button
            v-if="!ocultaImportes"
            class="exb-paid-btn"
            :disabled="confirmingExtension || rejectingExtension"
            @click="confirmExtensionPaid"
          >
            {{ confirmingExtension ? $t("reservasHc.pageReservation.confirming") : $t("reservasHc.pageReservation.markAsPaid") }}
          </button>
          <button
            class="exb-reject-btn"
            :disabled="rejectingExtension || confirmingExtension"
            @click="rejectExtension"
          >
            {{ rejectingExtension ? $t("reservasHc.pageReservation.rejecting") : $t("reservasHc.pageReservation.reject") }}
          </button>
        </div>
      </div>
      </transition>

      <!-- Observación de la reserva (p.ej. importada por iCal o nota del anfitrión): solo si existe -->
      <div v-if="reservationObservation" class="obs-banner">
        <div class="obs-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        </div>
        <div class="obs-content">
          <strong>{{ $t("reservasHc.pageReservation.observation") }}</strong>
          <span>{{ reservationObservation }}</span>
        </div>
      </div>

      <!-- Extras que el huésped ya compró (sus cargos), a la vista junto a la
           observación: antes solo salían entrando en la pestaña de cargos. -->
      <div v-if="extrasContratados.length" class="obs-banner obs-banner--extras">
        <div class="obs-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
        </div>
        <div class="obs-content">
          <strong>{{ $t("calendarsChatbotHc.calendar.extrasTitle") }}</strong>
          <span v-for="(x, i) in extrasContratados" :key="'ext-' + i">
            {{ x.cantidad > 1 ? x.cantidad + " × " : "" }}{{ x.nombre }}<template v-if="x.importe !== null && !ocultaImportes"> · <b>{{ formatearImporteExtra(x) }}</b></template>
          </span>
        </div>
      </div>

      <!-- Cargos de servicio del agente pendientes de cobro (late checkout, early check-in) -->
      <div
        v-for="c in pendingServiceCharges"
        :key="'svc-' + c.id"
        class="ext-banner ext-banner--requested"
      >
        <div class="exb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
        </div>
        <div class="exb-content">
          <strong>{{ chargeTitle(c) }} · {{ $t("reservasHc.pageReservation.pendingCharge") }}</strong>
          <span>
            {{ c.description }}<template v-if="chargeAmount(c)"> · <b>{{ chargeAmount(c) }}</b></template>
            · {{ $t("reservasHc.pageReservation.awaitingGuestPayment") }}
          </span>
        </div>
        <div class="exb-actions">
          <button
            v-if="!ocultaImportes"
            class="exb-paid-btn"
            :disabled="chargeBusy === c.id"
            @click="markChargePaid(c)"
          >
            {{ chargeBusy === c.id ? $t("reservasHc.pageReservation.processing") : $t("reservasHc.pageReservation.markAsPaid") }}
          </button>
          <button
            class="exb-reject-btn"
            :disabled="chargeBusy === c.id"
            @click="rejectCharge(c)"
          >
            {{ $t("reservasHc.pageReservation.reject") }}
          </button>
        </div>
      </div>

      <!-- Solicitudes de servicio "a consultar" pedidas por el huésped en el motor -->
      <div
        v-for="r in serviceRequests"
        :key="'sr-' + r.id"
        class="ext-banner"
        :class="Number(r.state) === 1 ? 'ext-banner--requested' : (Number(r.state) === 3 ? 'ext-banner--rejected' : 'ext-banner--approved')"
      >
        <div class="exb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/><path d="M12 8v4"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
        </div>
        <div class="exb-content">
          <strong>{{ $t("reservasHc.pageReservation.clientRequested") }} {{ r.servicio_nombre || $t("reservasHc.pageReservation.serviceFallback") }} · {{ $t("reservasHc.pageReservation.toConsult") }}</strong>
          <span>{{ srStateLabel(r) }}</span>
        </div>
        <div class="exb-actions" v-if="Number(r.state) === 1">
          <button class="exb-paid-btn" :disabled="srBusy === r.id" @click="openApproveModal(r)">
            {{ srBusy === r.id ? $t("reservasHc.pageReservation.processing") : $t("reservasHc.pageReservation.approve") }}
          </button>
          <button class="exb-reject-btn" :disabled="srBusy === r.id" @click="rejectServiceRequest(r)">
            {{ $t("reservasHc.pageReservation.reject") }}
          </button>
        </div>
      </div>

      <!-- Modal: aprobar solicitud "a consultar" (indicar importe a cobrar) -->
      <div v-if="srModal.open" class="sr-modal-overlay" @click.self="closeApproveModal">
        <div class="sr-modal">
          <h3 class="sr-modal__title">{{ $t("reservasHc.pageReservation.approveRequestTitle") }}</h3>
          <p class="sr-modal__svc">{{ (srModal.request && (srModal.request.servicio_nombre || $t("reservasHc.pageReservation.serviceFallback"))) }}</p>
          <label class="sr-modal__label">{{ $t("reservasHc.pageReservation.amountToCharge") }} <span class="sr-modal__opt">{{ $t("reservasHc.pageReservation.noCharge") }}</span></label>
          <div class="sr-modal__amount">
            <input type="number" min="0" step="0.01" inputmode="decimal" v-model="srModal.amount" placeholder="0.00" @keyup.enter="confirmApprove" />
            <span class="sr-modal__cur">{{ srCurrency() }}</span>
          </div>
          <p class="sr-modal__hint" v-html="$t('reservasHc.pageReservation.approveHint')"></p>
          <div class="sr-modal__actions">
            <button class="sr-modal__cancel" @click="closeApproveModal" :disabled="srBusy">{{ $t("reservasHc.pageReservation.cancel") }}</button>
            <button class="sr-modal__ok" @click="confirmApprove" :disabled="srBusy">
              {{ srBusy ? $t("reservasHc.pageReservation.processing") : $t("reservasHc.pageReservation.approveAndNotify") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modal: marcar como pagada (total vs adelanto %) -->
      <div v-if="markPaidModal.open" class="sr-modal-overlay" @click.self="closeMarkPaidModal">
        <div class="sr-modal">
          <h3 class="sr-modal__title">{{ $t("reservasHc.pageReservation.markAsPaid") }}</h3>
          <p class="sr-modal__svc">
            {{ $t("reservasHc.pageReservation.reservationTotal") }} <strong>{{ markPaidMoney(markPaidModal.baseTotal) }}</strong>
            <span class="sr-modal__opt">{{ $t("reservasHc.pageReservation.onRequestPaidSeparately") }}</span>
          </p>

          <label style="display:flex;align-items:center;gap:8px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:8px;cursor:pointer;"
                 :style="markPaidModal.mode==='full' ? 'border-color:#673de6;background:#f5f3ff;' : ''">
            <input type="radio" value="full" v-model="markPaidModal.mode" />
            <span><strong>{{ $t("reservasHc.pageReservation.fullPayment") }}</strong> · {{ markPaidMoney(markPaidModal.baseTotal) }} <small style="color:#16a34a;">{{ $t("reservasHc.pageReservation.noBalance") }}</small></span>
          </label>

          <label style="display:flex;align-items:center;gap:8px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:8px;cursor:pointer;"
                 :style="markPaidModal.mode==='partial' ? 'border-color:#673de6;background:#f5f3ff;' : ''">
            <input type="radio" value="partial" v-model="markPaidModal.mode" />
            <span><strong>{{ $t("reservasHc.pageReservation.deposit") }}</strong> · {{ markPaidMoney(markPaidPartialAmount) }}
              <small style="color:#b45309;">({{ $t("reservasHc.pageReservation.balance") }} {{ markPaidMoney(markPaidBalance) }})</small>
            </span>
          </label>

          <div v-if="markPaidModal.mode==='partial'" class="sr-modal__amount" style="max-width:140px;">
            <input type="number" min="1" max="100" step="1" inputmode="numeric" v-model.number="markPaidModal.percent" />
            <span class="sr-modal__cur">%</span>
          </div>

          <p class="sr-modal__hint">{{ $t("reservasHc.pageReservation.markPaidHint") }}</p>
          <div class="sr-modal__actions">
            <button class="sr-modal__cancel" @click="closeMarkPaidModal" :disabled="markingAsPaid">{{ $t("reservasHc.pageReservation.cancel") }}</button>
            <button class="sr-modal__ok" @click="confirmMarkPaid" :disabled="markingAsPaid">
              {{ markingAsPaid ? $t("reservasHc.pageReservation.marking") : $t("reservasHc.pageReservation.confirm") }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="isExpiredPaymentStatus" class="expired-status-banner">
        <div class="esb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 7v5"/>
            <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none"/>
          </svg>
        </div>
        <div class="esb-content">
          <strong>{{ $t("reservasHc.pageReservation.paymentExpiredTitle") }}</strong>
          <span>{{ $t("reservasHc.pageReservation.paymentExpiredText") }}</span>
        </div>
      </div>

      <div v-if="isOverbookingStatus" class="overbooking-status-banner">
        <div class="osb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"/>
          </svg>
        </div>
        <div class="osb-content">
          <strong>{{ $t("reservasHc.pageReservation.overbookingTitle") }}</strong>
          <span>{{ $t("reservasHc.pageReservation.overbookingText") }}</span>
        </div>
        <div class="osb-actions">
          <button
            class="osb-confirm-btn"
            :disabled="resolvingOverbooking"
            @click="confirmResolveOverbooking"
          >
            {{ resolvingOverbooking ? $t("reservasHc.pageReservation.verifying") : $t("reservasHc.pageReservation.confirmReservation") }}
          </button>
        </div>
      </div>

      <!-- Aviso puesto a mano para esta reserva (ver src/config/avisosPorReserva.js) -->
      <div v-if="avisoManual" class="overbooking-status-banner aviso-manual-banner">
        <div class="osb-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><circle cx="12" cy="17" r="1" fill="currentColor" stroke="none"/>
          </svg>
        </div>
        <div class="osb-content">
          <strong>{{ avisoManual.titulo }}</strong>
          <span>{{ avisoManual.texto }}</span>
        </div>
      </div>

      <div class="rsv-layout" :class="{ 'rsv-entrance': playEntrance }">
        <div class="rsv-main">
          <section class="rsv-card rsv-anim" style="--d: 0">
            <ReservationData
              :reservationData="reservationData"
              :formatDate="formatDate"
              :formatDates="formatDates"
              :getNights="getNights"
              :handleConfirmationCancellation="handleConfirmationCancellation"
              :cancellingBooking="cancellingBooking"
              @edit-reservation="handleEditReservation"
              @early-decided="fetchActivities"
            />

            <!-- Barra de acciones de check-in (en el header, como el diseño) -->
            <div v-if="canSendLink || canEditReservation" class="rsv-actions-bar">
              <Button
                v-if="canSendLink || sending"
                @click="confirmateSendCheckin()"
                class="rsv-btn rsv-btn--primary"
                :disabled="sending"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
                <strong>{{ $t("reservasHc.pageReservation.checkinInvitation") }}</strong>
              </Button>

              <!-- Copiar link de check-in: junto a Invitación -->
              <Button
                v-if="canSendLink"
                v-clipboard:copy="link"
                v-clipboard:success="onCopySuccess"
                v-clipboard:error="onCopyError"
                class="rsv-btn rsv-btn--ghost"
                :title="$t('reservasHc.pageReservation.copyCheckinLink')"
              >
                <i class="fa-solid fa-clone"></i>
                <strong>{{ $t("reservasHc.pageReservation.copyCheckinLink") }}</strong>
              </Button>

              <Button
                v-if="showPinButton"
                @click="confirmSendAccessPin()"
                class="rsv-btn"
                :disabled="sendingPin"
                :class="pinAlreadySent ? 'rsv-btn--neutral' : 'rsv-btn--purple'"
              >
                <i class="fa-solid fa-key"></i>
                <strong>{{ pinButtonText }}</strong>
              </Button>

              <Button
                v-if="showEarlyAccessButton"
                @click="confirmEarlyAccess()"
                class="rsv-btn rsv-btn--blue"
                :disabled="sendingEarlyAccess"
              >
                <i class="fa-solid fa-clock"></i>
                <strong>{{ earlyAccessButtonText }}</strong>
              </Button>

              <Button
                v-if="showStaticCodeButton"
                @click="confirmSendStaticCode()"
                class="rsv-btn"
                :disabled="sendingStaticCode"
                :class="staticCodeAlreadySent ? 'rsv-btn--neutral' : 'rsv-btn--green-soft'"
              >
                <i class="fa-solid fa-door-open"></i>
                <strong>{{ staticCodeButtonText }}</strong>
              </Button>

              <div class="rsv-actions-bar__spacer"></div>

              <!-- Editar reserva: a la derecha (donde estaba Copiar link).
                   También visible en el drawer del calendario (embedded): el drawer
                   eleva el contador de z-index de PrimeVue en onShow, así que el
                   modal de edición (Dialog) se muestra por encima del drawer. -->
              <Button
                v-if="canEditReservation"
                @click="handleEditReservation(reservationData)"
                class="rsv-btn rsv-btn--ghost"
                :title="$t('reservasHc.pageReservation.editReservation')"
              >
                <i class="fa-solid fa-pen"></i>
                <strong>{{ $t("reservasHc.pageReservation.editReservation") }}</strong>
              </Button>
            </div>
          </section>

          <!-- Reserva FLOTANTE (hotel confirmada sin habitación física): aviso +
               asignador in-situ (habitaciones libres del tipo para estas fechas). -->
          <section
            v-if="reservationData && reservationData.needs_room_assignment"
            class="rsv-anim"
            style="--d: 1"
          >
            <RoomAssignmentPanel
              :reserva-id="reserva"
              :user-id="id"
              @assigned="onRoomAssigned"
            />
          </section>

          <!-- Habitaciones de la reserva: SIEMPRE que haya líneas, no solo en las
               combinadas. Iba con `is_group`, así que una reserva de una sola línea con
               varias habitaciones del mismo tipo (PAULA: 2 habitaciones, la 106 y la
               107) no mostraba CUÁLES ocupaba. El propio GroupRoomsPanel ya decide qué
               texto usar y solo dice «Reserva combinada» si hay varios tipos. -->
          <section v-if="mostrarHabitacionesReserva" class="rsv-card rsv-anim" style="--d: 1">
            <GroupRoomsPanel
              :reservation-data="reservationData"
              :current-reserva-id="reservationData.id"
              @changed="onGroupChanged"
            />
          </section>

          <!-- Códigos y accesos del huésped (solo si existen) -->
          <section
            v-if="(canSendLink && (ttlockPinEntries.length || currentPinCode || shellyGuestAccessEntries.length || (pinValidFromLocal && pinValidToLocal))) || expiredPins.length"
            class="rsv-card rsv-anim"
            style="--d: 1"
          >
            <header class="rsv-card__head">
              <span class="rsv-card__icon rsv-card__icon--green">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </span>
              <div class="rsv-card__titles">
                <h3 class="rsv-card__title">{{ $t("reservasHc.pageReservation.codesAndAccess") }}</h3>
                <p class="rsv-card__sub">{{ $t("reservasHc.pageReservation.codesAndAccessSub") }}</p>
              </div>

              <!-- Validez como chip en el header -->
              <div v-if="pinValidFromLocal && pinValidToLocal" class="rsv-valid-chip">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/>
                </svg>
                <span>
                  <b>{{ pinValidFromLocal }}</b> → <b>{{ pinValidToLocal }}</b>
                  <small v-if="propertyTimezone"> · {{ propertyTimezone }}</small>
                </span>
              </div>
            </header>

            <div class="box-copy-link">
              <div
                class="form-group d-flex flex-column"
                style="gap: 0.5rem; width: 100%; justify-content: space-between"
              >
                <div v-if="ttlockPinEntries.length" class="mt-2 pin-display pin-display--ttlock">
                  <small class="pin-label">{{ $t("reservasHc.pageReservation.currentAccessCodes") }}</small>
                  <div class="ttlock-pin-list">
                    <div
                      v-for="entry in ttlockPinEntries"
                      :key="`${entry.role}-${entry.device_id || entry.device_name}`"
                      class="ttlock-pin-entry"
                    >
                      <div class="ttlock-pin-entry__meta">
                        <span class="ttlock-pin-entry__label">{{ entry.label }}</span>
                        <small v-if="entry.device_name">{{ entry.device_name }}</small>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <code>{{ entry.pin_code }}</code>
                        <Button
                          icon="pi pi-copy"
                          class="p-button-text p-button-sm"
                          v-clipboard:copy="entry.pin_code"
                          v-clipboard:success="onCopyPinSuccess"
                        >
                          <i class="fa-solid fa-clone" style="cursor: pointer"></i>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="currentPinCode" class="mt-2 pin-display">
                  <small class="pin-label">{{ $t("reservasHc.pageReservation.currentAccessCode") }}</small>
                  <div class="d-flex align-items-center gap-2">
                    <code>{{ currentPinCode }}</code>
                    <Button
                      icon="pi pi-copy"
                      class="p-button-text p-button-sm"
                      v-clipboard:copy="currentPinCode"
                      v-clipboard:success="onCopyPinSuccess"
                    >
                      <i class="fa-solid fa-clone" style="cursor: pointer"></i>
                    </Button>
                  </div>
                </div>

                <div
                  v-else-if="shellyGuestAccessEntries.length"
                  class="mt-2 access-block"
                >
                  <small class="access-block__label">{{ $t("reservasHc.pageReservation.guestDigitalAccess") }}</small>
                  <div class="access-link-list">
                    <div
                      v-for="entry in shellyGuestAccessEntries"
                      :key="entry.device_id || entry.url"
                      class="access-link-entry"
                    >
                      <span class="access-link-entry__icon">
                        <i class="fa-solid fa-door-open"></i>
                      </span>
                      <span class="access-link-entry__label">{{ entry.label }}</span>

                      <div class="access-link-entry__actions">
                        <button
                          type="button"
                          class="access-link-btn"
                          v-clipboard:copy="entry.url"
                          v-clipboard:success="onCopyPinSuccess"
                          :title="$t('reservasHc.pageReservation.copyOpenLink')"
                        >
                          <i class="fa-solid fa-clone"></i>
                          {{ $t("reservasHc.pageReservation.copy") }}
                        </button>
                        <a
                          :href="entry.url"
                          target="_blank"
                          rel="noopener"
                          class="access-link-btn access-link-btn--primary"
                          :title="$t('reservasHc.pageReservation.openLink')"
                        >
                          <i class="fa-solid fa-arrow-up-right-from-square"></i>
                          {{ $t("reservasHc.pageReservation.open") }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="isYacanAccess" class="mt-2 yacan-access-note">
                  <i class="fa-solid fa-circle-info"></i>
                  <span v-html="$t('reservasHc.pageReservation.yacanNote')"></span>
                </div>

                <div v-if="isYacanAccess && yacanEkey" class="mt-2 pin-display">
                  <small class="pin-label">{{ $t("reservasHc.pageReservation.digitalKeyEkey") }}</small>
                  <div class="d-flex align-items-center gap-2">
                    <a
                      :href="yacanEkey"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="yacan-ekey-link"
                    >
                      <i class="fa-solid fa-key"></i> {{ $t("reservasHc.pageReservation.openDigitalKey") }}
                    </a>
                    <Button
                      class="p-button-text p-button-sm"
                      v-clipboard:copy="yacanEkey"
                      v-clipboard:success="onCopyPinSuccess"
                    >
                      <i class="fa-solid fa-clone" style="cursor: pointer"></i>
                    </Button>
                  </div>
                </div>

                <!-- PIN ya caducados. Fuera de la cadena de arriba a propósito: es
                     información adicional, no una alternativa a los códigos
                     vigentes. Antes desaparecían al pasar la fecha y el anfitrión
                     creía que el huésped nunca los había recibido. -->
                <div v-if="expiredPins.length" class="mt-3 pin-expired">
                  <small class="pin-label">{{ $t("reservasHc.pageReservation.previousCodesExpired") }}</small>
                  <div
                    v-for="p in expiredPins"
                    :key="'exp-' + p.id"
                    class="pin-expired__row"
                  >
                    <code class="pin-expired__code">{{ p.pin_code }}</code>
                    <span class="pin-expired__meta">
                      <template v-if="p.device_name">{{ p.device_name }} · </template>
                      <template v-if="p.sent_at">{{ $t("reservasHc.pageReservation.sentOn") }} {{ p.sent_at }}</template>
                      <template v-else>{{ $t("reservasHc.pageReservation.generated") }}</template>
                      <template v-if="p.valid_to"> · {{ $t("reservasHc.pageReservation.expiredOn") }} {{ p.valid_to }}</template>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Importe y pago (ancho completo; los datos del responsable van en el header)
               Para quien no ve importes se quita la TARJETA ENTERA: vaciarla por dentro
               dejaría la cabecera, la insignia y dos separadores colgando de un hueco. -->
          <section v-if="!ocultaImportes" class="rsv-card rsv-anim" style="--d: 2">
            <header class="rsv-card__head">
              <span class="rsv-card__icon rsv-card__icon--purple">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z"/><polyline points="9 12 11 14 15 10"/>
                </svg>
              </span>
              <div class="rsv-card__titles">
                <h3 class="rsv-card__title">{{ $t("reservasHc.pageReservation.amountAndPayment") }}</h3>
                <p class="rsv-card__sub">{{ $t("reservasHc.pageReservation.amountAndPaymentSub") }}</p>
              </div>
            </header>
            <PayData :reservationData="reservationData" :user="user" />
          </section>

          <!-- Recorrido del huésped (timeline) -->
          <section class="rsv-card rsv-timeline rsv-anim" style="--d: 4">
            <header class="rsv-timeline__head">
              <div class="rsv-timeline__heading">
                <span class="rsv-timeline__icon">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/>
                  </svg>
                </span>
                <div>
                  <div class="rsv-timeline__titlerow">
                    <h3 class="rsv-timeline__title">{{ $t("reservasHc.pageReservation.guestJourney") }}</h3>
                    <span class="rsv-timeline__count">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      {{ $tc("reservasHc.pageReservation.eventsCount", timelineFeed.length, { count: timelineFeed.length }) }}
                    </span>
                  </div>
                  <p class="rsv-timeline__subtitle">{{ $t("reservasHc.pageReservation.journeySubtitle") }}</p>
                </div>
              </div>

              <div class="rsv-timeline__filters">
                <button
                  v-for="f in timelineFilters"
                  :key="f.key"
                  type="button"
                  class="rsv-timeline__filter"
                  :class="{ 'is-active': timelineFilter === f.key }"
                  @click="timelineFilter = f.key"
                >
                  {{ f.label }}
                </button>
              </div>
            </header>

            <!-- Eventos agrupados por día -->
            <transition name="rsv-tl" mode="out-in">
            <div class="rsv-timeline__list" :key="timelineFilter">
              <template v-for="(grp, gi) in timelineByDay">
                <div class="rsv-tl-day" :key="'day-' + gi">
                  <span class="rsv-tl-day__chip">{{ grp.day }}</span>
                  <span class="rsv-tl-day__rule"></span>
                </div>
                <div
                  v-for="(ev, i) in grp.items"
                  :key="ev.id"
                  class="rsv-tl-entry"
                  :style="{ '--i': ev.idx }"
                >
                  <div class="rsv-tl-entry__time">
                    <span v-if="ev.time" class="rsv-tl-entry__hour">{{ ev.time }}</span>
                  </div>
                  <div class="rsv-tl-entry__rail">
                    <span
                      v-if="!(gi === timelineByDay.length - 1 && i === grp.items.length - 1)"
                      class="rsv-tl-entry__line"
                    ></span>
                    <span class="rsv-tl-entry__dot" :class="'rsv-tl-entry__dot--' + ev.tone">
                      <i :class="ev.icon"></i>
                    </span>
                  </div>
                  <div
                    class="rsv-tl-entry__card"
                    :class="['rsv-tl-entry__card--' + ev.tone, { 'rsv-tl-entry__card--clickable': !!ev.onClick }]"
                    @click="ev.onClick && ev.onClick()"
                  >
                    <div class="rsv-tl-entry__row">
                      <span class="rsv-tl-entry__title">
                        <span class="rsv-tl-entry__who">{{ ev.who || guestFirstName }}</span>
                        <span class="rsv-tl-entry__action">{{ ev.action || ev.title }}</span>
                      </span>
                      <span v-if="ev.tag" class="rsv-tl-entry__tag" :class="'rsv-tl-entry__tag--' + ev.tone">{{ ev.tag }}</span>
                    </div>
                    <p v-if="ev.desc" class="rsv-tl-entry__desc">{{ ev.desc }}</p>

                    <!-- Diff de una modificación: viejo → nuevo por campo -->
                    <ul v-if="ev.changes && ev.changes.length" class="rsv-tl-changes">
                      <li v-for="(c, ci) in ev.changes" :key="ci" class="rsv-tl-change">
                        <span class="rsv-tl-change__label">{{ c.label }}</span>
                        <span class="rsv-tl-change__old">{{ c.old || '—' }}</span>
                        <span class="rsv-tl-change__arrow">→</span>
                        <span class="rsv-tl-change__new">{{ c.new || '—' }}</span>
                      </li>
                    </ul>

                    <span v-if="ev.onClick" class="rsv-tl-entry__chatlink">{{ $t("reservasHc.pageReservation.viewInChat") }} →</span>
                  </div>
                </div>
              </template>
            </div>
            </transition>
          </section>
        </div>

        <aside class="rsv-side rsv-anim" style="--d: 1">
          <ReservationConversations
            ref="convPanel"
            v-if="reservationData && reservationData.id"
            :whatsappChats="whatsappChats"
            :otaChats="otaChats"
            :webChats="webChats"
            :reservationData="reservationData"
            :limitedMode="conversationRestrictedStatus"
            :rejectedMode="isRejectedRequestStatus"
            :initialTab="initialTab"
            :initialChatId="initialChatId"
            @charges-changed="retrieveBooking"
          />
        </aside>
      </div>

      <ModalStatePayment />

      <!-- ── Modal: código de acceso estático ────────────────────────── -->
      <transition name="scm-fade">
        <div v-if="showStaticCodeModal" class="scm-overlay" @mousedown.self="showStaticCodeModal = false">
          <div class="scm-card" role="dialog" aria-modal="true">

            <!-- Header -->
            <div class="scm-header">
              <div class="scm-header-left">
                <div class="scm-header-icon">
                  <i class="fa-solid fa-door-open"></i>
                </div>
                <div>
                  <p class="scm-title">{{ $t("reservasHc.pageReservation.accessCode") }}</p>
                  <p class="scm-subtitle">{{ $t("reservasHc.pageReservation.accessCodeSub") }}</p>
                </div>
              </div>
              <button class="scm-close" @click="showStaticCodeModal = false" :title="$t('reservasHc.pageReservation.close')">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div class="scm-body">

              <!-- Código actual -->
              <div class="scm-section">
                <span class="scm-section-label">{{ $t("reservasHc.pageReservation.currentlyConfiguredCode") }}</span>
                <div class="scm-code-badge">
                  <i class="fa-solid fa-key"></i>
                  <span>{{ currentStaticCode || '—' }}</span>
                </div>
              </div>

              <!-- Input nuevo código -->
              <div class="scm-section">
                <label class="scm-section-label" for="scm-new-code-input">
                  {{ $t("reservasHc.pageReservation.newCode") }}
                  <span class="scm-section-note">{{ $t("reservasHc.pageReservation.keepSameToResend") }}</span>
                </label>
                <input
                  id="scm-new-code-input"
                  v-model="staticCodeInput"
                  type="text"
                  class="scm-input"
                  :placeholder="$t('reservasHc.pageReservation.placeholderCode')"
                  maxlength="60"
                  autocomplete="off"
                  spellcheck="false"
                  @input="staticCodeInput = staticCodeInput.toUpperCase()"
                />
                <p class="scm-hint-text">
                  <i class="fa-solid fa-circle-info"></i>
                  {{ $t("reservasHc.pageReservation.codeSavedUppercaseHint") }}
                </p>
              </div>
            </div>

            <!-- Footer -->
            <div class="scm-footer">
              <button class="scm-btn scm-btn--cancel" @click="showStaticCodeModal = false">
                {{ $t("reservasHc.pageReservation.cancel") }}
              </button>
              <button
                class="scm-btn scm-btn--primary"
                :disabled="sendingStaticCode || !staticCodeInput.trim()"
                @click="submitStaticCodeModal"
              >
                <i v-if="sendingStaticCode" class="fa-solid fa-circle-notch fa-spin"></i>
                <i v-else class="fa-solid fa-paper-plane"></i>
                {{ sendingStaticCode ? $t("reservasHc.pageReservation.sending") : $t("reservasHc.pageReservation.saveAndSendToGuest") }}
              </button>
            </div>

          </div>
        </div>
      </transition>
      <!-- ─────────────────────────────────────────────────────────────── -->

      <HotelReservationModal
        :visible.sync="showHotelReservationModal"
        :roomTypes="hotelRoomTypes"
        :presetStartDate="hotelPresetStartDate"
        :presetEndDate="hotelPresetEndDate"
        :presetRoomType="hotelPresetRoomType"
        :loader="hotelModalLoader"
        :editReservation="hotelBookingToEdit"
        :onCreateReservation="onSubmitHotelReservation"
        @changed="onGroupChanged"
      />
    </div>
  </div>
</template>

<script>
import ModalStatePayment from "@/components/Reservas/modalStatePayment.vue";
import toastr from "toastr";
import axios from "axios";
import $ from "jquery";
import moment from "moment";
import "moment/locale/es";

import ReservationData from "@/components/Reservas/ReservationData.vue";
import PayData from "@/components/Reservas/PayData.vue";
import GroupRoomsPanel from "@/components/Reservas/GroupRoomsPanel.vue";
import RoomAssignmentPanel from "@/components/Reservas/RoomAssignmentPanel.vue";
import ConfirmDialog from "primevue/confirmdialog";
import ReservationConversations from "./ReservationConversations.vue";
import Button from "primevue/button";
import HotelReservationModal from "@/components/Reservas/HotelReservationModal.vue";
import SocketioService from "@/services/socketio.service.js";
import { avisoDeReserva } from "@/config/avisosPorReserva";

export default {
  // initialTab/initialChatId: la bandeja de mensajes del nav abre esta vista dentro
  // del drawer pidiendo aterrizar en la pestaña del canal por el que escribió el
  // huésped (whatsapp | web | otas) y en esa conversación concreta.
  props: ["id", "reserva", "embedded", "initialTab", "initialChatId"],

  components: {
    ModalStatePayment,
    ReservationData,
    PayData,
    GroupRoomsPanel,
    ConfirmDialog,
    ReservationConversations,
    Button,
    HotelReservationModal,
  },

  data() {
    return {
      // Si se quita del grupo la habitación que se estaba viendo, el drawer pasa a
      // mostrar otra línea viva (override del id de la prop `reserva`).
      overrideReservaId: null,
      // Cuánto le queda al huésped para pagar la ampliación, ya en texto, y si va
      // apurado (<2 min). Lo mueve startExtensionCountdown(), igual que el contador
      // del pago de la reserva; se para en beforeDestroy.
      cuentaAtrasAmpliacion: null,
      plazoApurado: false,
      // Controla que la animación de entrada se reproduzca SOLO una vez (al montar).
      // Evita el parpadeo "desaparece/aparece" en re-renders o refrescos de datos.
      playEntrance: true,
      // Filtro activo del timeline (todo | whatsapp | checkin | acceso | booking)
      timelineFilter: "todo",
      // Historial real de la reserva (notas, edición, cancelación, etc.)
      activities: [],
      reservationData: {},
      user: null,
      link: "",
      cancellingBooking: false,
      sending: false,
      loadingChats: false,

      sendingPin: false,
      pinAlreadySent: false,
      currentPinCode: null,
      ttlockPinEntries: [],
      // PIN ya caducados: se muestran para dejar constancia de que se enviaron.
      expiredPins: [],
      shellyAccessLink: null,
      shellyAccessLinks: [],
      yacanEkey: null,
      earlyAccessEnabled: false,

      sendingEarlyAccess: false,
      canEarlyAccess: false,
      pinValidFromLocal: null,
      pinValidToLocal: null,
      propertyTimezone: null,

      sendingStaticCode: false,
      showStaticCodeModal: false,
      staticCodeInput: '',

      showHotelReservationModal: false,
      hotelRoomTypes: [],
      hotelRoomTypesPropertyId: null,
      hotelRoomTypesCacheKey: null,
      hotelModalLoader: false,
      hotelPresetStartDate: null,
      hotelPresetEndDate: null,
      hotelPresetRoomType: null,
      hotelBookingToEdit: null,

      approvingRequest: false,
      rejectingRequest: false,
      markingAsPaid: false,
      markingAsExpired: false,
      markPaidModal: { open: false, baseTotal: 0, currency: "EUR", percent: 30, mode: "full" },
      confirmingExtension: false,
      rejectingExtension: false,
      chargeBusy: null,
      srBusy: null,
      srModal: { open: false, request: null, amount: null },
      resolvingOverbooking: false,

      intentCountdown: null,
      _intentTimer: null,
    };
  },

  mounted() {
    // Garantiza que el socket esté conectado al entrar desde SPA (ej: desde Calendario)
    SocketioService.setupSocketConnection();
    // Forzar que App.vue re-bindee sus listeners si el userId todavía no estaba
    // disponible cuando conectó el socket (caso: navegar directo a esta URL).
    // Si ya están bindeados (lastBoundUserId === userId), App.vue lo ignora internamente.
    if (this.$root && this.$store.state.userRoot) {
      this.$root.$emit("__rebind-socket-listeners__");
    }
    this.retrieveBooking();
    this.listarUser();

    this.$store.state.updateComplete = false;

    // Realtime: si se confirma el pago de un servicio (late checkout / early check-in /
    // ampliación) de ESTA reserva, refrescamos importes, cargos y estado en vivo.
    this._onServicePayment = (p) => {
      try {
        const rid = p && p.reservation_id;
        if (!rid || Number(rid) === Number(this.reservationData && this.reservationData.id)) {
          this.retrieveBooking();
        }
      } catch (e) {
        console.warn("[Realtime] service-payment-confirmed (reserva) handler error", e);
      }
    };
    this.$root.$on("service-payment-confirmed", this._onServicePayment);

    /* El historial se mantiene solo, sin recargar la página.

       Un historial que hay que recargar a mano no es un historial: mandas la
       invitación y tienes que salir y volver a entrar para verla. Tres vías, de la
       más rápida a la más tozuda, porque no todos los caminos de envío avisan por
       socket (la invitación salió por uno que no avisa):

       1. el socket, cuando el aviso trae ESTA reserva → al instante;
       2. al volver a la pestaña, que es cuando el anfitrión mira;
       3. y un repaso cada 20 s mientras la ficha está abierta y a la vista.

       El repaso solo pide el historial (una lista corta) y se para cuando la
       pestaña no se ve, para no estar pidiendo de fondo todo el día. */
    this._refrescarHistorial = (payload) => {
      try {
        const suya = payload && (payload.reservation_id || payload.reserva_id || payload.booking_id);
        const mia = this.reservationData && this.reservationData.id;
        if (!mia) return;
        if (suya && Number(suya) !== Number(mia)) return;
        this.fetchActivities();
      } catch (e) {
        console.warn("[Timeline] no se pudo refrescar el historial", e);
      }
    };

    try {
      const socket = SocketioService.socket;
      const userId = this.$store.state.userRoot && this.$store.state.userRoot.id;
      if (socket) {
        socket.on("chat-message", this._refrescarHistorial);
        if (userId) socket.on(`chat-message-${userId}`, this._refrescarHistorial);
      }
    } catch (e) {
      console.warn("[Timeline] sin socket para el historial", e);
    }

    this._alVolverALaPestana = () => {
      if (document.visibilityState === "visible") this._refrescarHistorial();
    };
    document.addEventListener("visibilitychange", this._alVolverALaPestana);

    this._repasoHistorial = setInterval(() => {
      if (document.visibilityState === "visible") this._refrescarHistorial();
    }, 20000);

    // La entrada anima una sola vez; tras completarse, se desactiva para que
    // ningún refresco posterior vuelva a reproducir el fade.
    this._entranceTimer = setTimeout(() => {
      this.playEntrance = false;
    }, 1000);
  },

  beforeDestroy() {
    if (this._intentTimer) clearInterval(this._intentTimer);
    if (this._extTimer) clearInterval(this._extTimer);
    if (this._entranceTimer) clearTimeout(this._entranceTimer);
    if (this._onServicePayment) {
      this.$root.$off("service-payment-confirmed", this._onServicePayment);
    }
    if (this._repasoHistorial) clearInterval(this._repasoHistorial);
    if (this._alVolverALaPestana) {
      document.removeEventListener("visibilitychange", this._alVolverALaPestana);
    }
    if (this._refrescarHistorial) {
      try {
        const socket = SocketioService.socket;
        const userId = this.$store.state.userRoot && this.$store.state.userRoot.id;
        if (socket) {
          socket.off("chat-message", this._refrescarHistorial);
          if (userId) socket.off(`chat-message-${userId}`, this._refrescarHistorial);
        }
      } catch (e) {
        // el socket ya no está: nada que soltar
      }
    }
  },

  watch: {
    "$route.params.reserva"(newVal, oldVal) {
      if (newVal !== oldVal) this.retrieveBooking();
    },
    "$store.state.updateComplete"(val) {
      if (val) {
        this.retrieveBooking();
        this.$store.state.updateComplete = false;
      }
    },
    reservationData(val) {
      if (val) {
        this.languageDefault();
        this.checkPinStatus();
        this.startIntentCountdown();
        this.startExtensionCountdown();
      }
    },
  },

  computed: {
    /* ¿Hay que esconderle los importes a quien mira? Lo decide el store en un solo
       sitio (ver `ocultaImportes` en src/store/getters.js). Esto es solo para que no
       quede un hueco: el servidor ya no le manda los importes a este perfil. */
    ocultaImportes() {
      return this.$store.getters.ocultaImportes;
    },

    /**
     * El bloque de habitaciones SOLO en hotel. Al quitar la puerta `is_group` para
     * que se vieran también las reservas de una sola línea, se quedó sin ninguna: en
     * un apartamento la unidad ES el alojamiento entero, no hay habitaciones físicas
     * que enseñar, y salía un bloque que decía «1 habitación · Sin habitación
     * asignada» sin significar nada. El discriminador es unit_type === 'room'.
     */
    mostrarHabitacionesReserva() {
      const lineas = (this.reservationData && this.reservationData.group_lines) || [];
      if (!lineas.length) return false;

      return lineas.some((l) => String(l.unit_type || "") === "room");
    },
    property() {
      return this.$store.state.property.property;
    },

    // Observación de la reserva (campo `observacion`, p.ej. importado por iCal). Se
    // muestra en el detalle solo si tiene contenido.
    reservationObservation() {
      const o = this.reservationData && this.reservationData.observacion;
      return o && String(o).trim() ? String(o).trim() : "";
    },

    reservationChatsAll() {
      if (!this.reservationData?.id) return [];
      return this.$store.getters["chatbot/reservationChats"](this.reservationData.id) || [];
    },

    whatsappChats() {
      return this.reservationChatsAll.filter((c) => {
        const ch = String(c?.channel || "whatsapp").toLowerCase();
        return ch !== "web" && ch !== "ota";
      });
    },

    webChats() {
      return this.reservationChatsAll.filter(
        (c) => String(c?.channel || "").toLowerCase() === "web"
      );
    },

    otaChats() {
      return [];
    },

    normalizedStatus() {
      return String(this.reservationData?.status || "").trim().toLowerCase();
    },

    isPastReservation() {
      const raw = this.reservationData?.fecha_salida || this.reservationData?.fecha_ingreso;
      if (!raw) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(raw);
      target.setHours(0, 0, 0, 0);
      return target < today;
    },

    canSendLink() {
      const bookedStates = ["booked", "confirmed", "confirmada", "confirmado"];
      return !this.isPastReservation && bookedStates.includes(this.normalizedStatus);
    },

    // Cargos de servicio del agente (late checkout, early check-in) pendientes de
    // cobro: creados por el agente, aún sin pagar y sin facturar.
    pendingServiceCharges() {
      // OJO: la relación se serializa en snake_case → "invoice_charges".
      const charges = Array.isArray(this.reservationData?.invoice_charges)
        ? this.reservationData.invoice_charges
        : [];
      return charges.filter(
        (c) =>
          c &&
          typeof c.source === "string" &&
          c.source.indexOf("agent_") === 0 &&
          !c.paid_at &&
          !c.invoice_id
      );
    },

    // Extras comprados, los mismos que enseñan el calendario y el listado (backend:
    // AvisosDeLaReserva, con los cargos ya facturados incluidos). Los pedidos «a
    // consultar» que esperan respuesta no van aquí: tienen su aviso con botones.
    extrasContratados() {
      const extras = Array.isArray(this.reservationData?.extras_contratados)
        ? this.reservationData.extras_contratados
        : [];
      return extras.filter((x) => x && !x.pendiente);
    },

    // Solicitudes de servicio "a consultar" (on_request) que el huésped pidió en el
    // motor y están pendientes/gestionadas por el anfitrión (backend: retrieveBooking).
    serviceRequests() {
      return Array.isArray(this.reservationData?.service_requests)
        ? this.reservationData.service_requests
        : [];
    },

    // La reserva puede editarse salvo en estados de solicitud/intención/cancelada/etc.
    // (handleEditReservation hace no-op en esos estados; aquí ocultamos el botón).
    canEditReservation() {
      return !(
        this.isRequestStatus ||
        this.isIntentStatus ||
        this.isRejectedRequestStatus ||
        this.isExpiredPaymentStatus ||
        this.isCancelledStatus
      );
    },

    isRequestStatus() {
      return this.normalizedStatus === "request";
    },

    isOverbookingStatus() {
      return this.normalizedStatus === "overbooking";
    },
    avisoManual() {
      return avisoDeReserva(this.reservationData);
    },

    isIntentStatus() {
      return this.normalizedStatus === "intent";
    },

    // Ampliación pendiente en la MISMA reserva (intent = esperando pago,
    // requested = pendiente de aprobación del anfitrión).
    hasPendingExtension() {
      const s = String(this.reservationData?.extension_status || "").toLowerCase();
      return s === "intent" || s === "requested";
    },

    extensionPriceLabel() {
      // Sin importes no se enseña el precio de la ampliación; el hecho de que la
      // estancia se amplió sí, que eso es información de trabajo.
      if (this.ocultaImportes) return "";
      const price = this.reservationData?.extension_price;
      if (price === null || price === undefined || Number(price) <= 0) return "";
      const cur = this.reservationData?.propiedad?.currency || "";
      const sym = { EUR: "€", USD: "$", GBP: "£", BRL: "R$", COP: "$", MXN: "$" }[cur] || (cur ? cur + " " : "");
      return `${sym}${Number(price)}`;
    },

    isExpiredPaymentStatus() {
      return this.normalizedStatus === "expired";
    },

    isCancelledStatus() {
      return ["cancelled", "canceled", "cancelado", "cancelada"].includes(this.normalizedStatus);
    },

    isRejectedRequestStatus() {
      const status = this.normalizedStatus;
      const reason = String(
        this.reservationData?.rejection_reason ||
        this.reservationData?.cancel_reason ||
        this.reservationData?.cancellation_reason ||
        this.reservationData?.reason_cancel ||
        ""
      ).toLowerCase();

      return (
        status === "rejected" ||
        status === "request_rejected" ||
        status === "rejected_request" ||
        (status === "cancelled" && reason.includes("request")) ||
        (!!this.reservationData?.rejected_at)
      );
    },

    canMarkIntentAsPaid() {
      return this.isIntentStatus && !this.isExpiredPaymentStatus;
    },

    // Importe del adelanto (percent% del total base) y saldo restante, para el diálogo.
    markPaidPartialAmount() {
      const pct = Math.max(1, Math.min(100, Number(this.markPaidModal.percent) || 30));
      return Math.round((Number(this.markPaidModal.baseTotal) || 0) * pct) / 100;
    },
    markPaidBalance() {
      const bal = (Number(this.markPaidModal.baseTotal) || 0) - this.markPaidPartialAmount;
      return Math.max(0, Math.round(bal * 100) / 100);
    },

    canMarkIntentAsExpired() {
      return this.isIntentStatus;
    },

    conversationRestrictedStatus() {
      return (
        this.isRequestStatus ||
        this.isIntentStatus ||
        this.isRejectedRequestStatus ||
        this.isExpiredPaymentStatus ||
        this.isCancelledStatus
      );
    },

    accessTypeConnect() {
      return this.reservationData?.propiedad?.access_key_apartment?.type_connect || "";
    },

    isShellyAccess() {
      return this.accessTypeConnect === "shelly";
    },

    /* ¿Esta reserva tiene cerradura inteligente?

       `propiedad.smart_lock` es una bandera del ANUNCIO. En un hotel la cerradura se
       conecta a la HABITACIÓN y esa bandera se queda apagada, así que la ficha no
       enseñaba ni la llave ni el botón de reenviar el código aunque la cerradura
       estuviera conectada y funcionando (demo, 13-oct, Akiles en la Duplex H1).

       El backend ya manda la respuesta resuelta en `tiene_cerradura_inteligente`
       (configuración efectiva: habitación > unidad > anuncio). La bandera se sigue
       mirando para no cambiar el comportamiento de los alojamientos de siempre. */
    tieneCerraduraInteligente() {
      return (
        this.reservationData?.tiene_cerradura_inteligente === true ||
        /* 🚨 Y NO `this.tieneCerraduraInteligente`, que es esta misma propiedad: se
           llamaba a sí misma. Con una reserva SIN cerradura —casi todas— la primera
           condición da falso, pasa a la segunda y se muerde la cola: «Maximum call
           stack size exceeded» y la ficha entera en blanco. La bandera del ANUNCIO es
           lo que había que seguir mirando, que es lo que dice el comentario de arriba. */
        this.reservationData?.propiedad?.smart_lock === true
      );
    },

    isYacanAccess() {
      return (
        this.accessTypeConnect === "yacan" &&
        this.tieneCerraduraInteligente
      );
    },

    shellyGuestAccessEntries() {
      if (this.shellyAccessLinks.length > 1) return this.shellyAccessLinks;
      if (this.shellyAccessLink) {
        return [
          {
            device_id: this.shellyAccessLinks[0]?.device_id || 0,
            label: this.shellyAccessLinks[0]?.label || this.$t("reservasHc.pageReservation.doorLabel"),
            url: this.shellyAccessLink,
          },
        ];
      }
      return [];
    },

    showPinButton() {
      // 🚨 Akiles faltaba en esta lista: sin ella, el botón de reenviar el código no
      // sale aunque la cerradura esté conectada y la bandera bien.
      const isDynamic = ["dynamic", "tedee", "ttlock", "shelly", "yacan", "akiles"].includes(this.accessTypeConnect);
      return (
        this.canSendLink &&
        this.tieneCerraduraInteligente &&
        !!this.reservationData?.telefono &&
        isDynamic
      );
    },

    pinButtonText() {
      if (this.isShellyAccess || this.isYacanAccess) {
        if (this.sendingPin) return this.$t("reservasHc.pageReservation.sending");
        return this.pinAlreadySent
          ? this.$t("reservasHc.pageReservation.resendAccess")
          : this.$t("reservasHc.pageReservation.sendAccess");
      }
      if (this.sendingPin) return this.$t("reservasHc.pageReservation.sending");
      if (this.pinAlreadySent) return this.$t("reservasHc.pageReservation.resendPin");
      return this.$t("reservasHc.pageReservation.sendPin");
    },

    showEarlyAccessButton() {
      if (this.isShellyAccess) {
        return this.showPinButton && this.canEarlyAccess === true;
      }
      return this.showPinButton && !!this.currentPinCode && this.canEarlyAccess === true;
    },

    earlyAccessButtonText() {
      if (this.sendingEarlyAccess) return this.$t("reservasHc.pageReservation.activating");
      return "Early-Access";
    },

    // ── Código estático ──────────────────────────────────────────────────────
    currentStaticCode() {
      return this.reservationData?.propiedad?.access_key_apartment?.value_access || '';
    },

    showStaticCodeButton() {
      const accessKey = this.reservationData?.propiedad?.access_key_apartment;
      return (
        this.canSendLink &&
        this.tieneCerraduraInteligente &&
        !!this.reservationData?.telefono &&
        accessKey?.type_connect === "static" &&
        !!accessKey?.value_access
      );
    },

    staticCodeAlreadySent() {
      return !!this.reservationData?.static_code_sent_at;
    },

    staticCodeButtonText() {
      if (this.sendingStaticCode) return this.$t("reservasHc.pageReservation.sending");
      if (this.staticCodeAlreadySent) return this.$t("reservasHc.pageReservation.resendCode");
      return this.$t("reservasHc.pageReservation.sendAccessCode");
    },

    /* El parte de viajeros a las autoridades, que no vive en el historial de la
       reserva sino repartido por la ficha de cada huésped (`historial_firmas`):
       Mossos, Ertzaintza, Alloggiati, ROSS 1000, SIRE y TRA. Se toma el PRIMER envío
       como la hora del hecho y se nombran los servicios usados.

       Es el único evento que sigue armándose aquí, y solo porque su hora está en
       otro sitio. Todo lo demás llega ya hecho del historial: el timeline cuenta lo
       que pasó, no calcula etapas ni estados. */
    authorityEvents() {
      const r = this.reservationData || {};
      const registros = r.reservas || r.checkin || [];
      if (!registros.length) return [];

      const SERVICIOS = [
        ["mossos_sent_at", "Mossos"],
        ["ertzaintza_sent_at", "Ertzaintza"],
        ["alloggiati_reported_at", "Alloggiati"],
        ["ross1000_reported_at", "ROSS 1000"],
        ["sire_reported_at", "SIRE"],
        ["tra_reported_at", "TRA"],
      ];

      const envios = [];
      registros.forEach((c) => {
        if (!c) return;
        SERVICIOS.forEach(([campo, nombre]) => {
          if (c[campo]) envios.push({ ts: c[campo], nombre });
        });
      });

      if (!envios.length) return [];

      envios.sort((a, b) => moment(a.ts).valueOf() - moment(b.ts).valueOf());
      const primero = envios[0];
      const nombres = [...new Set(envios.map((e) => e.nombre))];
      const t = (k, pr) => this.$t("reservasHc.pageReservation." + k, pr);

      return [
        {
          id: "autoridades",
          ts: primero.ts,
          date: moment(primero.ts).format("DD MMM"),
          time: moment(primero.ts).format("HH:mm"),
          icon: "fa-solid fa-building-shield",
          tone: "purple",
          cat: "checkin",
          who: t("theTeam"),
          action: t("authoritiesAction"),
          desc: t("authoritiesDesc", { services: nombres.join(" · ") }),
        },
      ];
    },

    // Nombre de pila del huésped (para resaltar en cada evento del timeline)
    guestFirstName() {
      const name = String(this.reservationData?.nombre || "").trim();
      return name ? name.split(/\s+/)[0] : this.$t("reservasHc.pageReservation.theGuest");
    },

    // Eventos REALES del historial (no derivados) mapeados al formato del timeline
    activityEvents() {
      const fmt = (d) => (d ? moment(d).format("DD MMM") : "—");
      const fmtT = (d) => (d ? moment(d).format("HH:mm") : null);
      const styleMap = {
        edited: { icon: "fa-solid fa-pen", tone: "blue", cat: "booking" },
        cancelled: { icon: "fa-solid fa-circle-xmark", tone: "neutral", cat: "booking" },
        note: { icon: "fa-solid fa-note-sticky", tone: "purple", cat: "note" },
        invoice: { icon: "fa-solid fa-file-invoice", tone: "blue", cat: "booking" },
        charge: { icon: "fa-solid fa-tag", tone: "blue", cat: "booking" },
        payment: { icon: "fa-solid fa-credit-card", tone: "green", cat: "booking" },
        agent: { icon: "fa-solid fa-robot", tone: "purple", cat: "booking" },
        // Lo que decidió el anfitrión sobre una petición del agente (aprobar,
        // otra hora, priorizar, rechazar). Lo firma él, no el huésped.
        agent_decision: { icon: "fa-solid fa-user-check", tone: "purple", cat: "booking" },
        extension: { icon: "fa-solid fa-calendar-plus", tone: "green", cat: "booking" },
        whatsapp: { icon: "fa-brands fa-whatsapp", tone: "green", cat: "whatsapp" },
        call: { icon: "fa-solid fa-phone", tone: "purple", cat: "voz" },
        // Lo que le va pasando a la estancia. Llega del historial con su hora real;
        // antes el panel lo pintaba como «etapa» con un estado calculado y por eso
        // un envío hecho a mano salía como no enviado.
        booking: { icon: "fa-solid fa-bookmark", tone: "blue", cat: "booking" },
        invite_sent: { icon: "fa-solid fa-paper-plane", tone: "blue", cat: "checkin" },
        checkin: { icon: "fa-solid fa-id-card", tone: "purple", cat: "checkin" },
        code_sent: { icon: "fa-solid fa-key", tone: "green", cat: "acceso" },
        checkout: { icon: "fa-solid fa-door-open", tone: "neutral", cat: "booking" },
        ota: { icon: "fa-solid fa-comment-dots", tone: "blue", cat: "whatsapp" },
        web: { icon: "fa-solid fa-globe", tone: "blue", cat: "whatsapp" },
      };
      return (this.activities || [])
        .map((a) => {
          const s = styleMap[a.type] || {
            icon: "fa-solid fa-clock-rotate-left",
            tone: "blue",
            cat: "booking",
          };
          const meta = a.meta || {};
          // Quién originó el evento: el huésped (agente/whatsapp/ampliación del
          // agente) o el equipo (ediciones manuales).
          let who = a.user && a.user.name ? a.user.name : this.$t("reservasHc.pageReservation.theTeam");
          if (a.type === "agent" || a.type === "whatsapp") who = this.guestFirstName;
          if (a.type === "extension") who = meta.via === "agent" ? this.guestFirstName : who;
          if (a.type === "payment" && (meta.via === "guest" || meta.method === "online")) who = this.guestFirstName;
          // Cancelación: la firma quien la hizo de verdad. Si vino por el canal, el
          // canal; si la lanzó una tarea nuestra, el sistema; si no, quien la hizo.
          if (a.type === "cancelled") {
            if (meta.canal) who = meta.canal;
            else if (meta.origen === "ota") who = this.$t("reservasHc.pageReservation.theChannel");
            else if (meta.origen === "sistema") who = this.$t("reservasHc.pageReservation.theSystem");
          }
          // Llamada de voz: el que llamó es el huésped, y el texto lleva su número.
          if (a.type === "call") who = this.guestFirstName;
          // Lo que hace o recibe el huésped lo firma él. El check-in lo firma quien
          // lo hizo, que puede ser un acompañante y no el titular.
          if (["invite_sent", "code_sent", "checkout", "ota", "web"].includes(a.type)) {
            who = this.guestFirstName;
          }
          if (a.type === "checkin") who = a.guest_name || this.guestFirstName;
          if (a.type === "booking" && !(a.user && a.user.name)) who = this.guestFirstName;
          const title =
            a.type === "call"
              ? this.$t("reservasHc.pageReservation.calledFromNumber", { phone: meta.caller_phone || "" })
              : a.title;

          return {
            id: "act-" + a.id,
            ts: a.created_at,
            date: fmt(a.created_at),
            time: fmtT(a.created_at),
            icon: s.icon,
            tone: s.tone,
            cat: s.cat,
            tag: null,
            who,
            title: title,
            action: title,
            desc: a.type === "checkin" ? null : a.description,
            changes: Array.isArray(meta.changes) ? meta.changes : null,
            pending: false,
            isActivity: true,
            onClick:
              a.type === "call"
                ? () => this.openCallsTab()
                : a.type === "whatsapp"
                ? () => this.scrollToConversation()
                : null,
          };
        });
    },

    /* Todo lo que pasó, en el orden en que pasó. Nada de etapas pendientes: lo que
       todavía no ha ocurrido no es historial, y pintarlo en gris hacía que un envío
       hecho a mano pareciera no hecho. */
    timelineFeed() {
      return [...this.activityEvents, ...this.authorityEvents].sort((a, b) => {
        const ta = a.ts ? moment(a.ts).valueOf() : 0;
        const tb = b.ts ? moment(b.ts).valueOf() : 0;
        return ta - tb;
      });
    },

    // Pestañas de filtro del timeline
    timelineFilters() {
      return [
        { key: "todo", label: this.$t("reservasHc.pageReservation.filterAll") },
        { key: "whatsapp", label: this.$t("reservasHc.pageReservation.filterWhatsapp") },
        { key: "voz", label: this.$t("reservasHc.pageReservation.filterCalls") },
        { key: "checkin", label: this.$t("reservasHc.pageReservation.filterCheckin") },
        { key: "acceso", label: this.$t("reservasHc.pageReservation.filterAccess") },
        { key: "booking", label: this.$t("reservasHc.pageReservation.filterBooking") },
      ];
    },

    /**
     * La línea de tiempo: SOLO lo que ya ocurrió, en el orden en que ocurrió.
     *
     * Antes entraban también las etapas pendientes, y como no tienen fecha se les
     * ponía la de entrada o la de salida: aparecían bajo cabeceras de días futuros
     * («14 OCT», «17 OCT»), descolocadas y dejando huecos en blanco entre medias.
     * Una línea de tiempo cuenta lo que pasó; lo que falta se dice aparte.
     */
    timelineByDay() {
      const events = (
        this.timelineFilter === "todo"
          ? this.timelineFeed
          : this.timelineFeed.filter((e) => e.cat === this.timelineFilter)
      ).filter((e) => !e.pending);

      const groups = [];
      const map = {};
      // El índice es global y va topado: con 40 eventos, esperar 40 saltos para ver
      // el último no es una animación, es una espera.
      events.forEach((e, i) => {
        if (!map[e.date]) {
          map[e.date] = [];
          groups.push({ day: e.date, items: map[e.date] });
        }
        map[e.date].push({ ...e, idx: Math.min(i, 12) });
      });
      return groups;
    },

  },

  methods: {
    async approveRequest() {
      this.approvingRequest = true;
      try {
        await axios.post("/booking/approve-request", { booking_id: this.reservationData.id });
        toastr.success(this.$t("reservasHc.pageReservation.requestApprovedMsg"), this.$t("reservasHc.pageReservation.requestApprovedTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || e?.response?.data?.error || this.$t("reservasHc.pageReservation.couldNotApprove"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.approvingRequest = false;
      }
    },

    confirmRejectRequest() {
      this.$confirm.require({
        message: this.$t("reservasHc.pageReservation.confirmRejectRequestMsg"),
        header: this.$t("reservasHc.pageReservation.rejectRequestHeader"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: this.$t("reservasHc.pageReservation.yesReject"),
        rejectLabel: this.$t("reservasHc.pageReservation.cancel"),
        accept: () => this.rejectRequest(),
        reject: () => {},
      });
    },

    async rejectRequest() {
      this.rejectingRequest = true;
      try {
        await axios.post("/booking/reject-request", { booking_id: this.reservationData.id });
        toastr.warning(this.$t("reservasHc.pageReservation.requestRejectedMsg"), this.$t("reservasHc.pageReservation.rejectedTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || e?.response?.data?.error || this.$t("reservasHc.pageReservation.couldNotReject"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.rejectingRequest = false;
      }
    },

    // Abre el diálogo "Marcar como pagada": carga el total base (sin los "a consultar") y
    // el % configurado; el operador elige total o adelanto.
    async openMarkPaidModal() {
      try {
        const { data } = await axios.post("/booking/mark-intent-paid/options", {
          booking_id: this.reservationData.id,
        });
        this.markPaidModal = {
          open: true,
          baseTotal: Number(data?.base_total || 0),
          currency: data?.currency || "EUR",
          percent: Number(data?.percent || 30),
          mode: "full",
        };
      } catch (e) {
        toastr.warning(this.$t("reservasHc.pageReservation.couldNotLoadOptions"), this.$t("reservasHc.pageReservation.noticeTitle"));
        await this.confirmMarkPaidWith("full", null);
      }
    },
    closeMarkPaidModal() {
      if (this.markingAsPaid) return;
      this.markPaidModal = { ...this.markPaidModal, open: false };
    },
    async confirmMarkPaid() {
      const mode = this.markPaidModal.mode === "partial" ? "partial" : "full";
      const percent = mode === "partial"
        ? Math.max(1, Math.min(100, Number(this.markPaidModal.percent) || 30))
        : null;
      await this.confirmMarkPaidWith(mode, percent);
    },
    async confirmMarkPaidWith(mode, percent) {
      this.markingAsPaid = true;
      try {
        await axios.post("/booking/mark-intent-paid", {
          booking_id: this.reservationData.id,
          mode,
          ...(percent != null ? { percent } : {}),
        });
        toastr.success(
          mode === "partial"
            ? this.$t("reservasHc.pageReservation.depositMarkedPaid")
            : this.$t("reservasHc.pageReservation.fullMarkedPaid"),
          this.$t("reservasHc.pageReservation.paymentConfirmedTitle")
        );
        this.markPaidModal = { ...this.markPaidModal, open: false };
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || e?.response?.data?.error || this.$t("reservasHc.pageReservation.couldNotMarkPaid"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.markingAsPaid = false;
      }
    },
    markPaidMoney(v) {
      if (this.ocultaImportes) return "—";
      const cur = this.markPaidModal.currency || "EUR";
      const n = Number(v) || 0;
      try {
        return new Intl.NumberFormat("es-ES", { useGrouping: "always", style: "currency", currency: cur }).format(n);
      } catch (e) {
        return `${cur} ${n.toFixed(2)}`;
      }
    },

    async markIntentAsExpired() {
      this.markingAsExpired = true;
      try {
        await axios.post("/booking/mark-intent-expired", {
          booking_id: this.reservationData.id,
        });
        toastr.warning(this.$t("reservasHc.pageReservation.tempReservationExpired"), this.$t("reservasHc.pageReservation.reservationExpiredTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || e?.response?.data?.error || this.$t("reservasHc.pageReservation.couldNotMarkExpired"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.markingAsExpired = false;
      }
    },

    async confirmExtensionPaid() {
      this.confirmingExtension = true;
      try {
        await axios.post("/booking/confirm-extension", { booking_id: this.reservationData.id });
        toastr.success(this.$t("reservasHc.pageReservation.extensionMarkedPaid"), this.$t("reservasHc.pageReservation.extensionConfirmedTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || e?.response?.data?.error || this.$t("reservasHc.pageReservation.couldNotConfirmExtension"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.confirmingExtension = false;
      }
    },

    async rejectExtension() {
      this.rejectingExtension = true;
      try {
        await axios.post("/booking/revert-extension", { booking_id: this.reservationData.id });
        toastr.warning(this.$t("reservasHc.pageReservation.extensionCancelled"), this.$t("reservasHc.pageReservation.extensionRejectedTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || e?.response?.data?.error || this.$t("reservasHc.pageReservation.couldNotRejectExtension"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.rejectingExtension = false;
      }
    },

    formatExtDate(d) {
      if (!d) return "—";
      try {
        return new Date(String(d).slice(0, 10) + "T00:00:00")
          .toLocaleDateString("es", { day: "2-digit", month: "short", year: "numeric" });
      } catch (_) {
        return String(d);
      }
    },

    // ── Cargos de servicio del agente (late checkout / early check-in) ──
    chargeTitle(c) {
      const src = String(c?.source || "");
      if (src === "agent_late_checkout") return "Late checkout";
      if (src === "agent_early_checkin") return "Early check-in";
      return c?.description || this.$t("reservasHc.pageReservation.serviceFallback");
    },

    chargeAmount(c) {
      // El cargo sigue saliendo (para saber QUÉ se pidió), pero sin su importe.
      if (this.ocultaImportes) return "";
      const amount = Number(c?.total_with_vat ?? 0);
      if (!amount) return "";
      const cur = (c?.currency || this.reservationData?.propiedad?.currency || "").toUpperCase();
      const sym = { EUR: "€", USD: "$", GBP: "£", BRL: "R$", COP: "$", MXN: "$" }[cur] || (cur ? cur + " " : "");
      return `${sym}${amount}`;
    },

    formatearImporteExtra(x) {
      const n = Number(x.importe).toLocaleString("es-ES", { useGrouping: "always", minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `${n} ${x.moneda || ""}`.trim();
    },

    async markChargePaid(c) {
      if (!c || this.chargeBusy) return;
      this.chargeBusy = c.id;
      try {
        await axios.post(`/charges/${c.id}/mark-paid`);
        toastr.success(this.$t("reservasHc.pageReservation.chargeMarkedPaid"), this.$t("reservasHc.pageReservation.chargeRegisteredTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotMarkChargePaid"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.chargeBusy = null;
      }
    },

    async rejectCharge(c) {
      if (!c || this.chargeBusy) return;
      this.chargeBusy = c.id;
      try {
        await axios.post(`/charges/${c.id}/reject`);
        toastr.warning(this.$t("reservasHc.pageReservation.chargeRejected"), this.$t("reservasHc.pageReservation.chargeRejectedTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotRejectCharge"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.chargeBusy = null;
      }
    },

    // ── Solicitudes de servicio "a consultar" ──────────────────────────────
    srStateLabel(r) {
      const s = Number(r?.state);
      if (s === 2) return this.$t("reservasHc.pageReservation.approvedState");
      if (s === 3) return this.$t("reservasHc.pageReservation.rejectedState");
      if (s === 4) return this.$t("reservasHc.pageReservation.paidState");
      return this.$t("reservasHc.pageReservation.pendingYourResponse");
    },
    srCurrency() {
      return (this.reservationData?.propiedad?.currency || "EUR").toUpperCase();
    },
    // Aprobar: abre el modal para indicar el importe a cobrar (0 = sin cargo).
    openApproveModal(r) {
      if (!r) return;
      this.srModal = { open: true, request: r, amount: null };
    },
    closeApproveModal() {
      this.srModal = { open: false, request: null, amount: null };
    },
    async confirmApprove() {
      const r = this.srModal.request;
      if (!r || this.srBusy) return;
      const raw = this.srModal.amount;
      const amount = raw === null || raw === "" ? 0 : Number(raw);
      if (isNaN(amount) || amount < 0) {
        toastr.error(this.$t("reservasHc.pageReservation.enterValidAmount"), this.$t("reservasHc.pageReservation.amountTitle"));
        return;
      }
      this.srBusy = r.id;
      try {
        const { data } = await axios.post("/service-request/approve", { id: r.id, amount });
        if (data?.payment_url) {
          toastr.success(this.$t("reservasHc.pageReservation.approvedPaymentLinkSent"), this.$t("reservasHc.pageReservation.serviceApprovedTitle"));
        } else if (amount > 0) {
          toastr.success(this.$t("reservasHc.pageReservation.approvedPayOnArrival"), this.$t("reservasHc.pageReservation.serviceApprovedTitle"));
        } else {
          toastr.success(this.$t("reservasHc.pageReservation.approvedGuestNotified"), this.$t("reservasHc.pageReservation.serviceApprovedTitle"));
        }
        this.closeApproveModal();
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotApproveRequest"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.srBusy = null;
      }
    },
    async rejectServiceRequest(r) {
      if (!r || this.srBusy) return;
      this.srBusy = r.id;
      try {
        await axios.post("/service-request/reject", { id: r.id });
        toastr.warning(this.$t("reservasHc.pageReservation.requestRejectedNotified"), this.$t("reservasHc.pageReservation.serviceRejectedTitle"));
        await this.retrieveBooking();
      } catch (e) {
        toastr.error(e?.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotRejectRequest"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.srBusy = null;
      }
    },

    startIntentCountdown() {
      if (this._intentTimer) clearInterval(this._intentTimer);
      this.intentCountdown = null;

      if (!this.isIntentStatus || !this.reservationData?.hold_expires_at) return;

      const updateCountdown = () => {
        const diff = new Date(this.reservationData.hold_expires_at).getTime() - Date.now();
        if (diff <= 0) {
          this.intentCountdown = null;
          clearInterval(this._intentTimer);
          this.retrieveBooking();
          return;
        }
        const m = Math.floor(diff / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        this.intentCountdown = `${m}:${String(s).padStart(2, "0")}`;
      };

      updateCountdown();
      this._intentTimer = setInterval(updateCountdown, 1000);
    },

    /**
     * Cuánto le queda al huésped para pagar la ampliación.
     *
     * Mismo patrón que startIntentCountdown(): un solo intervalo, armado cuando hay
     * una ampliación esperando el pago y parado en cuanto vence o se sale de la
     * pantalla. Antes esto era un computed con un reloj que corría en TODAS las
     * reservas, hubiera ampliación o no.
     *
     * El vencimiento llega en ISO con su zona, así que la cuenta sale bien aunque el
     * anfitrión esté en otro huso que el alojamiento — que pasa a diario con los
     * gestores.
     */
    startExtensionCountdown() {
      if (this._extTimer) clearInterval(this._extTimer);
      this.cuentaAtrasAmpliacion = null;
      this.plazoApurado = false;

      const iso = this.reservationData?.extension_hold_expires_at;
      if (!iso || String(this.reservationData?.extension_status || "") !== "intent") return;

      const vence = new Date(iso).getTime();
      if (Number.isNaN(vence)) return;

      let vencido = false;
      const tic = () => {
        const quedan = Math.floor((vence - Date.now()) / 1000);

        // Vencido: se dice, en vez de dejar la tarjeta como si siguiera esperando.
        if (quedan <= 0) {
          if (this._extTimer) clearInterval(this._extTimer);
          this._extTimer = null;
          vencido = true;
          this.plazoApurado = false;
          this.cuentaAtrasAmpliacion = this.$t("reservasHc.pageReservation.holdExpired");
          return;
        }

        this.plazoApurado = quedan < 120;

        /* Por encima del minuto, minutos redondeados HACIA ARRIBA: al huésped se le
           prometen 20 y con Math.floor el panel ponía «quedan 19 min» desde el primer
           segundo. Por debajo, segundos: es cuando el anfitrión decide si lo marca
           como pagado a mano o lo deja caer. El corte lo decide lo que queda de
           verdad, no el minuto redondeado (que con ceil nunca llega a 0). */
        this.cuentaAtrasAmpliacion = quedan >= 60
          ? this.$t("reservasHc.pageReservation.holdLeftMinutes", { min: Math.ceil(quedan / 60) })
          : this.$t("reservasHc.pageReservation.holdLeftSeconds", { seg: quedan });
      };

      tic();
      // El primer tic corre ANTES de que exista el intervalo: si el plazo ya había
      // vencido, no se arma ninguno.
      if (!vencido) this._extTimer = setInterval(tic, 1000);
    },

    async checkPinStatus() {
      if (!this.reservationData?.id || !this.showPinButton) return;
      try {
        const response = await axios.get("/smart-lock/reservation/pin-status", {
          params: { reservation_id: this.reservationData.id },
        });
        if (response.data?.success) {
          const d = response.data.data || {};
          this.ttlockPinEntries = this.normalizeTtlockPinEntries(
            d.provider === "ttlock" ? d.pin_entries || [] : []
          );
          this.yacanEkey = null;

          if (d.provider === "shelly") {
            // Shelly: enlace de apertura remota en lugar de PIN
            this.currentPinCode = null;
            this.ttlockPinEntries = [];
            this.shellyAccessLink = d.access_link || null;
            this.shellyAccessLinks = Array.isArray(d.access_links) ? d.access_links : [];
            this.pinAlreadySent = !!d.pin_sent_manually || !!d.access_sent;
            this.earlyAccessEnabled = !!d.early_access_enabled;
          } else if (d.provider === "yacan") {
            // YACAN: código de teclado + ekey (llave digital)
            this.ttlockPinEntries = [];
            this.shellyAccessLink = null;
            this.shellyAccessLinks = [];
            this.currentPinCode = d.pin_code || null;
            this.yacanEkey = d.access_link || null;
            this.pinAlreadySent = !!d.pin_sent_manually || !!d.access_sent;
          } else if (d.has_pin) {
            this.shellyAccessLink = null;
            this.shellyAccessLinks = [];
            this.pinAlreadySent = !!d.pin_sent_manually;
            this.currentPinCode = this.ttlockPinEntries.length > 0
              ? null
              : (d.pin_code || null);
          } else {
            this.shellyAccessLink = null;
            this.shellyAccessLinks = [];
            this.currentPinCode = null;
            this.pinAlreadySent = !!d.pin_sent_manually;
            this.ttlockPinEntries = [];
          }
          this.canEarlyAccess = !!d.can_early_access;
          this.pinValidFromLocal = d.valid_from_local || null;
          this.pinValidToLocal = d.valid_to_local || null;
          this.propertyTimezone = d.property_timezone || null;
          // Caducados: se guardan aparte para poder decir que SÍ se enviaron.
          // Antes desaparecían del panel al pasar la fecha y parecía que no.
          this.expiredPins = Array.isArray(d.pins_history)
            ? d.pins_history.filter((p) => p && p.expired)
            : [];
        }
      } catch (error) {
        console.error("Error verificando estado del PIN:", error);
      }
    },

    confirmSendAccessPin() {
      let message;
      if (this.isShellyAccess) {
        message = this.pinAlreadySent
          ? this.$t("reservasHc.pageReservation.resendAccessLinkMsg")
          : this.$t("reservasHc.pageReservation.sendAccessLinkMsg");
      } else {
        message = this.pinAlreadySent
          ? this.$t("reservasHc.pageReservation.resendPinMsg")
          : this.$t("reservasHc.pageReservation.sendPinMsg");
      }
      this.$confirm.require({
        message,
        header: this.isShellyAccess
          ? this.$t("reservasHc.pageReservation.sendDigitalAccessHeader")
          : this.$t("reservasHc.pageReservation.sendPinHeader"),
        icon: "pi pi-key",
        acceptLabel: this.$t("reservasHc.pageReservation.send"),
        rejectLabel: this.$t("reservasHc.pageReservation.cancel"),
        accept: () => this.sendAccessPin(),
        reject: () => {},
      });
    },

    async sendAccessPin() {
      this.sendingPin = true;
      try {
        const response = await axios.post("/smart-lock/reservation/send-pin", {
          reservation_id: this.reservationData.id,
          force_new: this.pinAlreadySent,
        });
        if (response.data?.success) {
          const data = response.data.data;
          this.pinAlreadySent = true;

          if (data.provider === "shelly") {
            // Shelly: enlace de apertura remota, sin PINs
            this.shellyAccessLink = data.access_link || null;
            this.shellyAccessLinks = Array.isArray(data.access_links) ? data.access_links : [];
            this.currentPinCode = null;
            this.ttlockPinEntries = [];

            toastr.success(this.$t("reservasHc.pageReservation.accessLinkSent"), this.$t("reservasHc.pageReservation.accessSentTitle"));
            const channels = this.buildAccessChannelSummary({
              email: !!data.email_delivery?.queued,
              ota: !!data.ota_delivery?.sent,
              whatsapp: Array.isArray(data.messages_sent)
                ? data.messages_sent.some((item) => item?.sent)
                : !!this.reservationData?.telefono,
            });
            this.$toast?.add({
              severity: "success",
              summary: this.$t("reservasHc.pageReservation.successSummary"),
              detail: this.$t("reservasHc.pageReservation.digitalAccessSentDetail", { count: data.locks_count }) + (channels ? ". " + channels : ""),
              life: 5000,
            });
            await this.checkPinStatus();
            return;
          }

          this.ttlockPinEntries = this.normalizeTtlockPinEntries(data.passcodes_created || []);
          this.currentPinCode = this.ttlockPinEntries.length > 0
            ? null
            : (data.pin_code || null);
          const messageText = this.ttlockPinEntries.length > 1
            ? this.$t("reservasHc.pageReservation.ttlockCodesGenerated", { count: this.ttlockPinEntries.length })
            : data.reused_existing
              ? this.$t("reservasHc.pageReservation.accessCodeResent", { code: data.pin_code })
              : this.$t("reservasHc.pageReservation.accessCodeGeneratedSent", { code: data.pin_code });
          toastr.success(messageText, this.$t("reservasHc.pageReservation.pinSentTitle"));
          const channels = this.buildAccessChannelSummary({
            email: !!data.email_delivery?.queued,
            ota: !!data.ota_delivery?.sent,
            whatsapp: Array.isArray(data.messages_sent)
              ? data.messages_sent.some((item) => item?.sent)
              : !!this.reservationData?.telefono,
          });
          this.$toast?.add({
            severity: "success",
            summary: this.$t("reservasHc.pageReservation.successSummary"),
            detail: this.$t("reservasHc.pageReservation.pinProcessedDetail", { count: data.locks_count })
              + (data.blebox_count > 0 ? " " + this.$t("reservasHc.pageReservation.pinProcessedBlebox", { count: data.blebox_count }) : "")
              + (channels ? ". " + channels : ""),
            life: 5000,
          });
          await this.checkPinStatus();
        }
      } catch (error) {
        console.error("Error enviando PIN:", error);
        toastr.error(error.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotSendAccessCode"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.sendingPin = false;
      }
    },

    onCopyPinSuccess() {
      toastr.success(this.$t("reservasHc.pageReservation.codeCopied"), this.$t("reservasHc.pageReservation.successSummary"));
    },

    normalizeTtlockPinEntries(entries = []) {
      return [...entries]
        .filter((entry) => entry && (entry.pin_code || entry.passcode))
        .map((entry) => ({
          ...entry,
          pin_code: entry.pin_code || entry.passcode,
          label: entry.label || (entry.role === "entrance" ? this.$t("reservasHc.pageReservation.mainGate") : this.$t("reservasHc.pageReservation.accommodation")),
        }))
        .sort((left, right) => {
          const leftOrder = left.role === "entrance" ? 0 : 1;
          const rightOrder = right.role === "entrance" ? 0 : 1;
          return leftOrder - rightOrder;
        });
    },

    confirmEarlyAccess() {
      if (this.sendingEarlyAccess) return;
      const windowText = this.pinValidFromLocal && this.pinValidToLocal
        ? "\n\n" + this.$t("reservasHc.pageReservation.validUntil", { date: this.pinValidToLocal }) : "";
      const baseMessage = this.isShellyAccess
        ? this.$t("reservasHc.pageReservation.earlyAccessLinkMsg")
        : this.$t("reservasHc.pageReservation.earlyAccessPinMsg");
      this.$confirm.require({
        message: baseMessage + windowText,
        header: this.$t("reservasHc.pageReservation.earlyAccessHeader"),
        icon: "pi pi-clock",
        acceptLabel: this.$t("reservasHc.pageReservation.activate"),
        rejectLabel: this.$t("reservasHc.pageReservation.cancel"),
        accept: () => this.enableEarlyAccess(),
        reject: () => {},
      });
    },

    async enableEarlyAccess() {
      if (!this.reservationData?.id) return;
      this.sendingEarlyAccess = true;
      try {
        const res = await axios.post("/smart-lock/reservation/early-access", {
          reservation_id: this.reservationData.id,
        });
        if (res.data?.success) {
          toastr.success(this.$t("reservasHc.pageReservation.earlyAccessActivated"), this.$t("reservasHc.pageReservation.successSummary"));
          await this.checkPinStatus();
          this.$toast?.add({ severity: "success", summary: this.$t("reservasHc.pageReservation.okSummary"), detail: this.$t("reservasHc.pageReservation.earlyAccessActivatedDetail"), life: 4000 });
        } else {
          toastr.error(res.data?.message || this.$t("reservasHc.pageReservation.couldNotActivateEarlyAccess"), this.$t("reservasHc.pageReservation.error"));
        }
      } catch (e) {
        toastr.error(e?.response?.data?.message || e?.message || this.$t("reservasHc.pageReservation.couldNotActivateEarlyAccess"), this.$t("reservasHc.pageReservation.error"));
      } finally {
        this.sendingEarlyAccess = false;
      }
    },

    normalizeType(v) {
      return String(v ?? "").trim().toLowerCase();
    },

    getReservationPropertyType(reservation) {
      const p = reservation?.propiedad || {};
      const raw = p?.type ?? p?.tipo ?? p?.property_type ?? p?.accommodation_type ?? reservation?.propiedad_type ?? reservation?.property_type ?? "";
      return this.normalizeType(raw);
    },

    // Unidades (habitaciones) de una propiedad del store; tolerante a la clave.
    propertyUnits(prop) {
      return prop?.accommodationUnits || prop?.accommodation_units || [];
    },

    // ¿La propiedad tiene alguna unidad tipo habitación? → es un hotel.
    propertyHasRooms(prop) {
      return this.propertyUnits(prop).some(
        (u) => String(u?.type || "").toLowerCase() === "room"
      );
    },

    // Localiza la unidad de la reserva entre las propiedades del store (que traen
    // sus unidades) y devuelve la unidad + la propiedad DUEÑA (que puede diferir de
    // reservation.propiedad si los datos están desalineados).
    findReservationUnit(reservation) {
      const unitId =
        reservation?.accommodation_unit_id ??
        reservation?.accommodation_unit?.id ??
        reservation?.unit_id ??
        null;
      if (unitId == null) return { unit: null, ownerId: null };
      const props = this.$store.state.property?.properties || [];
      for (const p of props) {
        const u = this.propertyUnits(p).find(
          (x) => Number(x?.id) === Number(unitId)
        );
        if (u) return { unit: u, ownerId: p.id };
      }
      return { unit: null, ownerId: null };
    },

    // Tipo de la unidad que ocupa la reserva ('room' | 'entire_property' | '').
    getReservationUnitType(reservation) {
      const embedded = String(reservation?.accommodation_unit?.type || "").toLowerCase();
      if (embedded) return embedded;
      const { unit } = this.findReservationUnit(reservation);
      return String(unit?.type || "").toLowerCase();
    },

    isHotelLikeReservation(reservation) {
      // 1) Señal fuerte y fiable: la reserva ocupa una HABITACIÓN (unidad 'room').
      const unitType = this.getReservationUnitType(reservation);
      if (unitType === "room") return true;
      if (unitType === "entire_property") return false;

      // 2) ¿La propiedad de la reserva tiene habitaciones? → es un hotel.
      const p = reservation?.propiedad || {};
      const pid = p?.id ?? reservation?.propiedad_id ?? null;
      const prop =
        pid != null
          ? (this.$store.state.property?.properties || []).find(
              (sp) => Number(sp.id) === Number(pid)
            )
          : null;
      if (this.propertyHasRooms(prop)) return true;

      // 3) Último respaldo: el tipo declarado de la propiedad.
      const t = this.getReservationPropertyType(reservation);
      if (!t) return false;
      const hotelish = ["hotel", "hostel", "motel", "resort", "inn", "pousada", "camping", "campground", "camp"];
      const apartmentish = ["apartment", "apartamento", "villa", "vivienda", "flat", "house", "casa", "vacation_rental", "rental"];
      if (hotelish.some((k) => t === k || t.includes(k))) return true;
      if (apartmentish.some((k) => t === k || t.includes(k))) return false;
      return false;
    },

    async handleConfirmationCancellation() {
      if (
        this.isRequestStatus ||
        this.isIntentStatus ||
        this.isRejectedRequestStatus ||
        this.isExpiredPaymentStatus ||
        this.isCancelledStatus
      ) {
        return;
      }

      if (this.reservationData.id_channel) {
        this.$confirm.require({
          message: this.$t("empresa.reservas.p153"),
          header: this.$t("empresa.reservas.p154"),
          icon: "pi pi-exclamation-triangle",
          rejectLabel: this.$t("empresa.reservas.p155"),
          rejectClass: "p-button-secondary",
          reject: () => {},
          accept: null,
          acceptLabel: null,
          acceptIcon: null,
          acceptClass: "hidden-button",
          acceptStyle: { display: "none" },
          acceptProps: { style: { display: "none" } },
        });
        setTimeout(() => {
          const acceptButtons = document.querySelectorAll(".p-confirm-dialog-accept");
          acceptButtons.forEach((btn) => (btn.style.display = "none"));
        }, 50);
        return;
      }

      this.$confirm.require({
        // Aviso claro: una reserva puede ocupar varias habitaciones (de uno o
        // varios tipos). Cancelarla las cancela y libera TODAS, no solo una.
        message: this.$t("reservasHc.pageReservation.cancelReservationMsg"),
        header: this.$t("empresa.reservas.p73"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: this.$t("empresa.reservas.p73"),
        rejectLabel: this.$t("reservasHc.pageReservation.goBack"),
        acceptClass: "p-button-danger",
        accept: () => this.handleBookingCancellation(),
        reject: () => {},
      });
    },

    confirmResolveOverbooking() {
      this.$confirm.require({
        message: this.$t("reservasHc.pageReservation.resolveOverbookingMsg"),
        header: this.$t("reservasHc.pageReservation.confirmOverbookingHeader"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: this.$t("reservasHc.pageReservation.confirmReservation"),
        rejectLabel: this.$t("reservasHc.pageReservation.goBack"),
        accept: () => this.resolveOverbooking(),
        reject: () => {},
      });
    },

    async resolveOverbooking() {
      this.resolvingOverbooking = true;
      try {
        const { data } = await axios.post("/booking/resolve-overbooking", {
          booking_id: this.reservationData.id,
        });
        this.reservationData.status = data?.booking?.status || "booked";
        toastr.success(data?.message || this.$t("reservasHc.pageReservation.reservationConfirmed"));
      } catch (error) {
        const msg = error?.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotConfirmReservation");
        toastr.error(msg);
      } finally {
        this.resolvingOverbooking = false;
      }
    },

    async handleBookingCancellation() {
      this.cancellingBooking = true;
      try {
        const payload = { booking_id: this.reservationData.id };
        const { data } = await axios.post("/booking/cancelled", payload);
        this.reservationData.status = "cancelled";
        // El backend cancela TODO el grupo (multi-habitación) y devuelve cuántas filas.
        const n = Array.isArray(data?.cancelled_ids) ? data.cancelled_ids.length : 1;
        if (n > 1) {
          toastr.success(this.$t("reservasHc.pageReservation.reservationCancelledFreed", { count: n }));
        }
        // El backend acaba de anotar la cancelación en el historial; sin esto el
        // anfitrión no la ve hasta recargar, justo en la acción que más importa.
        this.fetchActivities();
      } catch (error) {
        toastr.error(error, this.$t("empresa.reservas.p156"));
      } finally {
        this.cancellingBooking = false;
      }
    },

    formatDates(fechaIngreso, fechaSalida) {
      const ingreso = moment(fechaIngreso).format("D MMM YYYY");
      const salida = moment(fechaSalida).format("D MMM YYYY");
      return `${ingreso} - ${salida}`;
    },

    getNights(fechaIngreso, fechaSalida) {
      return moment(fechaSalida).diff(moment(fechaIngreso), "days");
    },

    formatDate(date) {
      return moment(date).format("DD MMM YYYY");
    },

    languageDefault() {
      const p = this.reservationData?.propiedad || this.property || {};
      const code = (p?.code_pais || "").toUpperCase();
      let language;

      if (["AE", "SA", "MA", "EG", "IQ", "JO", "LB", "LY", "TN", "SD", "YE", "SY"].includes(code)) language = "ar";
      else if (code === "IT" || code === "ITA") language = "it";
      else if (["DE", "AT"].includes(code)) language = "de";
      else if (["BR", "PT"].includes(code)) language = "pt";
      else if (["FR", "BE"].includes(code)) language = "fr";
      else if (["ES", "CO", "MX", "AR", "CL", "VE", "PE", "EC", "PA", "CR", "GT", "HN", "SV", "NI", "DO", "PR", "UY"].includes(code)) language = "es";
      else language = "en";

      this.link = `${process.env.VUE_APP_GUEST}/${this.reservationData?.additional_data?.token}?lang=${language}`;
    },

    onCopySuccess() {
      toastr.success(this.$t("empresa.reservas.p157"), this.$t("empresa.reservas.p158"));
    },

    onCopyError() {
      toastr.error(this.$t("empresa.reservas.p159"), this.$t("empresa.reservas.p160"));
    },

    listarUser() {
      const url = this.$base.dominio + "/user";
      axios.get(url).then((res) => {
        this.user = res.data;
      });
    },

    confirmateSendCheckin() {
      this.$confirm.require({
        message: this.$t("empresa.reservas.p161"),
        header: this.$t("empresa.reservas.p166"),
        icon: "pi pi-exclamation-info",
        acceptLabel: this.$t("empresa.reservas.p163"),
        accept: () => this.sendCheckin(),
        reject: () => {},
      });
    },

    isSupportedOtaReservation() {
      return [1, 2].includes(Number(this.reservationData?.id_channel || 0));
    },

    async resolvePreferredCheckinLanguage() {
      if (!this.isSupportedOtaReservation()) return null;

      const phone = String(this.reservationData?.telefono || "").trim();
      if (phone) return null;

      const answer = window.prompt(
        this.$t("reservasHc.pageReservation.otaLangPrompt"),
        "en"
      );

      if (answer === null) {
        return "__cancel__";
      }

      const normalized = String(answer).trim().toLowerCase();
      return ["es", "en", "fr", "pt", "ar", "it", "de"].includes(normalized) ? normalized : "en";
    },

    async sendCheckin() {
      this.sending = true;
      const mensaje_correo =
        this.$t("empresa.enviarcheckin.text19") + (this.name ? this.name : "") +
        this.$t("empresa.enviarcheckin.text20") + (this.property?.nombre || "") +
        this.$t("empresa.enviarcheckin.text21");

      const preferredLang = await this.resolvePreferredCheckinLanguage();
      if (preferredLang === "__cancel__") {
        this.sending = false;
        return;
      }

      const url = this.$base.dominio + "/send/checkin-link-programed";
      const datosValidate = {
        user_id: this.$store.state.userRoot.id,
        propiedad_id: this.reservationData.propiedad.id,
        reserva_id: this.reservationData.id,
        nombre_propiedad: this.reservationData.propiedad.nombre,
        mensaje_correo,
        link: null,
        nombre: this.reservationData.nombre,
        email: this.reservationData.email,
        phone: this.reservationData.telefono,
        cantidad_huesped: this.reservationData.cantidad_huesped,
        preferred_lang: preferredLang,
      };

      try {
        const response = await axios.post(url, datosValidate);
        const delivery = response?.data?.delivery || {};
        const emailQueued = delivery?.email?.queued === true;
        const otaSent = delivery?.ota?.sent === true;

        this.listarUser();

        // Real-time: refresca la conversación abierta para que la plantilla de
        // check-in recién enviada aparezca sin recargar. ReservationConversations
        // escucha este bus y, sin `conversation`, re-consulta la conversación activa.
        this.$root.$emit("crm-socket-conversation-payload", {
          source: "checkin-link-sent",
          payload: {
            reserva_id: this.reservationData.id,
            reservation_id: this.reservationData.id,
            channel: "whatsapp",
          },
        });

        if (otaSent && emailQueued) {
          toastr.success(this.$t("reservasHc.pageReservation.inviteProcessedEmailOta"));
        } else if (otaSent) {
          toastr.success(this.$t("reservasHc.pageReservation.inviteProcessedOta"));
        } else if (emailQueued) {
          toastr.success(this.$t("reservasHc.pageReservation.inviteProcessedEmail"));
        } else {
          toastr.success(this.$t("empresa.reservas.p65"));
        }
      } catch (error) {
        toastr.error(
          error?.response?.data?.error || error?.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotSendCheckinInvite"),
          this.$t("reservasHc.pageReservation.error")
        );
      } finally {
        this.sending = false;
      }
    },

    async fetchReservationChats() {
      if (!this.reservationData || !this.reservationData.id) return;
      this.loadingChats = true;
      try {
        const reservationId = this.reservationData.id;
        const propertyId = this.reservationData?.propiedad?.id || "all";
        const userId = this.$store.state.userRoot.id;
        await this.$store.dispatch("chatbot/retrieveReservationChats", {
          user_id: userId,
          property_id: propertyId,
          reservation_id: reservationId,
          limit: 50,
        });
      } catch (e) {
        console.error(e);
        this.$toast?.add({
          severity: "error",
          summary: this.$t("reservasHc.pageReservation.error"),
          detail: this.$t("empresa.reservas.p164") || this.$t("reservasHc.pageReservation.couldNotLoadConversations"),
          life: 3000,
        });
      } finally {
        this.loadingChats = false;
      }
    },

    // Tras editar el grupo (quitar/cambiar cantidad de una habitación) refresca el
    // detalle. Si la habitación que se estaba viendo fue la que se quitó, el drawer
    // pasa a mostrar otra línea viva del grupo (la primaria).
    onGroupChanged(payload) {
      if (
        payload &&
        payload.removedId &&
        this.reservationData &&
        Number(payload.removedId) === Number(this.reservationData.id) &&
        payload.fallbackId
      ) {
        this.overrideReservaId = payload.fallbackId;
      }
      this.retrieveBooking();
      // Avisar al contenedor (calendario/lista) para que refresque disponibilidad/barras.
      this.$emit("reservation-updated");
    },

    // Tras asignar habitación a una reserva flotante desde el panel in-situ:
    // recarga el detalle (needs_room_assignment pasa a false → el panel se oculta y
    // la habitación ya aparece) y avisa por el bus para que el calendario refresque
    // su carril "Por organizar" si está montado.
    onRoomAssigned() {
      this.retrieveBooking();
      this.$root.$emit("hotel-reservation-assigned", { reserva_id: this.reserva });
    },

    async retrieveBooking() {
      try {
        const rid = this.overrideReservaId || this.reserva;
        const params = { reserva_id: rid, user_id: this.id };
        const response = await axios.get("/retrieve/booking", { params });
        this.reservationData = response.data;
        await this.fetchReservationChats();
        this.fetchActivities();
      } catch (error) {
        this.$toast?.add({
          severity: "error",
          summary: this.$t("reservasHc.pageReservation.error"),
          detail: this.$t("empresa.reservas.p164"),
          life: 3000,
        });
      }
    },

    // Historial real de la reserva (se fusiona con los eventos derivados del timeline)
    async fetchActivities() {
      const id = this.reservationData?.id;
      if (!id) return;
      try {
        const { data } = await axios.get(`/reservations/${id}/activities`);
        this.activities = Array.isArray(data?.data) ? data.data : [];
      } catch (e) {
        this.activities = [];
      }
    },

    // Click en el evento de WhatsApp del timeline → lleva al panel de chat.
    scrollToConversation() {
      const el = this.$refs.convPanel && this.$refs.convPanel.$el;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },

    // Click en una llamada del timeline → abre la pestaña "Llamadas" del panel.
    openCallsTab() {
      this.scrollToConversation();
      this.$nextTick(() => {
        const panel = this.$refs.convPanel;
        if (panel && typeof panel.switchTab === "function") {
          panel.switchTab("llamadas");
        }
      });
    },

    pad2(n) {
      return n < 10 ? "0" + n : "" + n;
    },

    parseYmdLocal(s) {
      const [y, m, d] = String(s || "").split("-").map(Number);
      if (!y || !m || !d) return null;
      return new Date(y, m - 1, d);
    },

    ymdLocalFromDate(d) {
      return `${d.getFullYear()}-${this.pad2(d.getMonth() + 1)}-${this.pad2(d.getDate())}`;
    },

    getMonthWindowFromYmd(ymd) {
      const dt = this.parseYmdLocal(ymd) || new Date();
      const first = new Date(dt.getFullYear(), dt.getMonth(), 1);
      const last = new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
      return {
        start_date: this.ymdLocalFromDate(first),
        end_date: this.ymdLocalFromDate(last),
      };
    },

    mapUnitsToRoomTypes(units) {
      return (Array.isArray(units) ? units : []).map((unit) => ({
        id: unit.id,
        name: unit.name,
        capacity: unit.capacity,
        available_instances: Array.isArray(unit.room_instances) ? unit.room_instances.length : 0,
        type: unit.type || null,
      }));
    },

    async loadHotelRoomTypes(propertyId, preferredWindowYmd = null) {
      if (!propertyId) {
        this.hotelRoomTypes = [];
        this.hotelRoomTypesPropertyId = null;
        this.hotelRoomTypesCacheKey = null;
        return;
      }

      const baseYmd =
        preferredWindowYmd ||
        this.reservationData?.fecha_ingreso ||
        this.reservationData?.fecha_salida ||
        null;

      const { start_date, end_date } = this.getMonthWindowFromYmd(
        baseYmd || this.ymdLocalFromDate(new Date())
      );

      const cacheKey = `${Number(propertyId)}:${start_date}:${end_date}`;
      if (this.hotelRoomTypesCacheKey === cacheKey && this.hotelRoomTypes.length) return;

      this.hotelRoomTypesPropertyId = Number(propertyId);
      this.hotelRoomTypesCacheKey = cacheKey;
      this.hotelRoomTypes = [];

      try {
        const res = await axios.post("/calendar-data-hotel", {
          property_id: Number(propertyId),
          user_id: this.$store?.state?.userRoot?.id,
          start_date,
          end_date,
        });

        const properties = Array.isArray(res.data) ? res.data : [];
        const prop = properties[0] || properties.find((p) => Number(p.id) === Number(propertyId)) || null;
        this.hotelRoomTypes = this.mapUnitsToRoomTypes(prop?.units || []);
      } catch (e) {
        console.warn("No pude cargar room types desde /calendar-data-hotel", e);
        this.hotelRoomTypes = [];
      }
    },

    async onSubmitHotelReservation(payload) {
      this.hotelModalLoader = true;
      try {
        const booking_id = this.hotelBookingToEdit?.id || this.reservationData?.id;
        await axios.post("/booking/update", { ...payload, booking_id });
        this.$toast?.add({ severity: "success", summary: this.$t("reservasHc.pageReservation.okSummary"), detail: this.$t("reservasHc.pageReservation.reservationUpdatedShort"), life: 2500 });
        this.showHotelReservationModal = false;
        this.hotelBookingToEdit = null;
        await this.retrieveBooking();
        return true;
      } catch (e) {
        console.error(e);
        this.$toast?.add({ severity: "error", summary: this.$t("reservasHc.pageReservation.error"), detail: this.$t("reservasHc.pageReservation.couldNotUpdateReservation"), life: 3500 });
        return false;
      } finally {
        this.hotelModalLoader = false;
      }
    },

    // ── Código estático ──────────────────────────────────────────────────────

    confirmSendStaticCode() {
      // Abre el modal mostrando el código actual para poder editarlo antes de enviar
      this.staticCodeInput = this.currentStaticCode;
      this.showStaticCodeModal = true;
    },

    async submitStaticCodeModal() {
      const code = this.staticCodeInput.trim().toUpperCase();
      if (!code) {
        toastr.warning(this.$t("reservasHc.pageReservation.codeEmpty"), this.$t("reservasHc.pageReservation.attentionTitle"));
        return;
      }
      this.showStaticCodeModal = false;
      await this.sendStaticCode(code);
    },

    async sendStaticCode(newCode = null) {
      this.sendingStaticCode = true;
      try {
        const payload = { reservation_id: this.reservationData.id };

        // Solo enviar new_code si es diferente al actual (evita guardar el mismo valor)
        if (newCode && newCode !== this.currentStaticCode) {
          payload.new_code = newCode;
        }

        const response = await axios.post("/smart-lock/reservation/send-static-code", payload);

        if (response.data?.success) {
          const data = response.data?.data || {};
          const delivery = data.delivery || {};
          const channels = this.buildAccessChannelSummary({
            email: !!delivery.email?.queued,
            ota: !!delivery.ota?.sent,
            whatsapp: !!delivery.whatsapp?.sent,
          });

          // Actualizar el código en reservationData para que el modal muestre el nuevo valor
          if (data.static_code && this.reservationData?.propiedad?.access_key_apartment) {
            this.$set(
              this.reservationData.propiedad.access_key_apartment,
              'value_access',
              data.static_code
            );
          }

          toastr.success(this.$t("reservasHc.pageReservation.accessCodeProcessed"), this.$t("reservasHc.pageReservation.sentCheckTitle"));
          this.$toast?.add({
            severity: "success",
            summary: this.$t("reservasHc.pageReservation.successSummary"),
            detail: channels || this.$t("reservasHc.pageReservation.accessCodeSendProcessed"),
            life: 5000,
          });
          await this.retrieveBooking();
        } else {
          toastr.error(response.data?.message || this.$t("reservasHc.pageReservation.couldNotSendCode"), this.$t("reservasHc.pageReservation.error"));
        }
      } catch (error) {
        toastr.error(
          error?.response?.data?.message || this.$t("reservasHc.pageReservation.couldNotSendAccessCode"),
          this.$t("reservasHc.pageReservation.error")
        );
      } finally {
        this.sendingStaticCode = false;
      }
    },

    buildAccessChannelSummary({ email = false, ota = false, whatsapp = false } = {}) {
      const channels = [];
      if (whatsapp) channels.push("WhatsApp");
      if (ota) channels.push(this.$t("reservasHc.pageReservation.chatOta"));
      if (email) channels.push(this.$t("reservasHc.pageReservation.emailChannel"));

      if (!channels.length) {
        return "";
      }

      return this.$t("reservasHc.pageReservation.channelsUsed", { channels: channels.join(", ") });
    },

    async handleEditReservation(reservation) {
      if (
        this.isRequestStatus ||
        this.isIntentStatus ||
        this.isRejectedRequestStatus ||
        this.isExpiredPaymentStatus ||
        this.isCancelledStatus
      ) {
        return;
      }

      const isHotel = this.isHotelLikeReservation(reservation);
      if (isHotel) {
        // Cargamos las habitaciones de la propiedad DUEÑA de la unidad de la reserva
        // (puede diferir de reservation.propiedad si los datos están desalineados);
        // si no se localiza, caemos a la propiedad de la reserva.
        const { ownerId } = this.findReservationUnit(reservation);
        const propertyId =
          ownerId || reservation?.propiedad?.id || reservation?.propiedad_id || null;
        this.hotelBookingToEdit = reservation;
        this.hotelPresetStartDate = reservation.fecha_ingreso;
        this.hotelPresetEndDate = reservation.fecha_salida;
        this.hotelPresetRoomType = null;

        const wantedUnitId =
          reservation?.accommodation_unit_id ||
          reservation?.room_type_id ||
          reservation?.unit_id ||
          reservation?.accommodation_unit?.id ||
          null;

        await this.loadHotelRoomTypes(propertyId, reservation.fecha_ingreso);

        if (wantedUnitId && this.hotelRoomTypes.length) {
          const rt = this.hotelRoomTypes.find((x) => Number(x.id) === Number(wantedUnitId));
          this.hotelPresetRoomType = rt || null;
        }

        this.showHotelReservationModal = true;
        return;
      }

      const range = {
        start: reservation.fecha_ingreso,
        end: reservation.fecha_salida,
        propertyId: reservation.propiedad_id,
      };

      this.$store.commit("SET_SELECTED_BOOKING_RANGE", range);
      this.$store.commit("SET_PROPERTY_SELECT", reservation.propiedad_id);
      this.$store.commit("SET_BOOKING_TO_EDIT", reservation);
      this.$store.commit("SET_MODAL_BOOKING_VISIBILITY", true);
    },
  },
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap");

/* ══════════════════════════════════════════════════════════════════
   Sistema de diseño — Detalle de reserva
   Base blanca · grises de apoyo · marca: #11b14d / #5e72e4 / #673de6
   ══════════════════════════════════════════════════════════════════ */
.page-reservation {
  --rsv-green: #11b14d;
  --rsv-green-700: #0a8a3a;
  --rsv-green-50: #e7f7ee;
  --rsv-green-100: #cdefd9;

  --rsv-blue: #5e72e4;
  --rsv-blue-700: #4054c8;
  --rsv-blue-50: #eef0fc;
  --rsv-blue-100: #dee2f9;

  --rsv-purple: #673de6;
  --rsv-purple-700: #5028c7;
  --rsv-purple-50: #f0eafd;
  --rsv-purple-100: #e1d6fb;

  --rsv-ink: #0e1320;
  --rsv-ink-2: #3a4156;
  --rsv-ink-3: #6b7388;
  --rsv-ink-4: #9aa2b5;

  --rsv-line: #ecedf2;
  --rsv-line-2: #f3f4f8;

  --rsv-shadow-xs: 0 1px 2px rgba(16, 24, 40, 0.05);
  --rsv-shadow-sm: 0 1px 2px rgba(16, 24, 40, 0.04), 0 6px 16px -6px rgba(16, 24, 40, 0.08);
  --rsv-shadow-md: 0 4px 12px -4px rgba(16, 24, 40, 0.1), 0 12px 32px -8px rgba(16, 24, 40, 0.1);
}

/* ── Layout principal: contenido flexible + panel derecho fijo ── */
.rsv-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 49%;
  gap: 18px;
  align-items: start;
}

.rsv-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

/* Fila de dos cards (Responsable + Pago) lado a lado */
.rsv-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}

.rsv-row > .rsv-card { height: 100%; }

/* ── Cards ── */
.rsv-card {
  background: #ffffff;
  border: 1px solid var(--rsv-line);
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}

.rsv-card:hover {
  box-shadow: 0 6px 22px rgba(16, 24, 40, 0.07), 0 2px 6px rgba(16, 24, 40, 0.04);
}

/* ── Modo embebido (drawer del calendario): layout más compacto y sobrio ── */
.page-reservation.is-embedded {
  .rsv-layout { gap: 15px; }
  .rsv-main { gap: 13px; }
  .rsv-card {
    padding: 16px 18px;
    border-radius: 14px;
  }
  /* el hover de la tarjeta no aporta dentro del drawer: lo dejamos plano */
  .rsv-card:hover {
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    transform: none;
  }
}

.rsv-card__head {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 13px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--rsv-line-2);
}

.rsv-card__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid transparent;
}

.rsv-card__icon--green { background: var(--rsv-green-50); color: var(--rsv-green-700); border-color: var(--rsv-green-100); }
.rsv-card__icon--blue  { background: var(--rsv-blue-50);  color: var(--rsv-blue-700);  border-color: var(--rsv-blue-100); }
.rsv-card__icon--purple{ background: var(--rsv-purple-50);color: var(--rsv-purple-700);border-color: var(--rsv-purple-100); }

.rsv-card__titles { min-width: 0; }

.rsv-card__title {
  margin: 0;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--rsv-ink);
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.rsv-card__sub {
  margin: 1px 0 0;
  font-family: "DM Sans", sans-serif;
  font-size: 11.5px;
  color: var(--rsv-ink-3);
  line-height: 1.35;
}

/* ── Panel derecho fijo (sticky) ── */
.rsv-side {
  position: sticky;
  top: 80px;
  align-self: start;
  height: calc(100vh - 96px);
  min-height: 560px;
  /* En el drawer del calendario no hay navbar arriba: el panel arranca pegado
     al tope del cuerpo scrollable y ocupa casi todo el alto del drawer. */
  .page-reservation.is-embedded & {
    top: 0;
    height: calc(100vh - 100px);
    min-height: 440px;
  }
  min-width: 0;
  background: #ffffff;
  border: 1px solid var(--rsv-line);
  border-radius: 18px;
  box-shadow: var(--rsv-shadow-sm);
  padding: 13px 13px 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.rsv-side > * {
  flex: 1;
  min-height: 0;
}

/* ── Menú de secciones (Check-ins / Cargos / Notas) ── */
.rsv-secmenu {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
}
.rsv-secmenu__item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 15px;
  background: #ffffff;
  border: 1px solid var(--rsv-line);
  border-radius: 14px;
  box-shadow: var(--rsv-shadow-xs);
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  text-align: left;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}
.rsv-secmenu__item:hover {
  transform: translateY(-1px);
  box-shadow: var(--rsv-shadow-sm);
  border-color: var(--rsv-line);
}
.rsv-secmenu__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-size: 0.86rem;
  flex-shrink: 0;
}
.rsv-secmenu__icon--green { background: var(--rsv-green-50); color: var(--rsv-green); }
.rsv-secmenu__icon--purple { background: var(--rsv-purple-50); color: var(--rsv-purple); }
.rsv-secmenu__icon--blue { background: var(--rsv-blue-50); color: var(--rsv-blue); }
.rsv-secmenu__label {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--rsv-ink);
}
.rsv-secmenu__badge {
  font-size: 11px;
  font-weight: 700;
  color: var(--rsv-ink-3);
  background: var(--rsv-line-2);
  padding: 2px 8px;
  border-radius: 999px;
}
.rsv-secmenu__chev {
  color: var(--rsv-ink-4);
  flex-shrink: 0;
}

/* ── Animaciones de entrada (escalonadas) ── */
@keyframes rsvFadeUp {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* La animación de entrada SOLO se aplica mientras el layout tiene .rsv-entrance
   (primer render). Después se desactiva, así un refresco/re-render no vuelve a
   reproducir el fade (evita el "desaparece y aparece"). */
.rsv-entrance .rsv-anim {
  /* 'backwards' (no 'both'): la tarjeta se anima al entrar pero NO retiene el
     transform al terminar. Con 'both' quedaba translateY(0) fijo → un transform
     (aunque sea 0) crea un stacking context que ATRAPABA los modales (fixed) y
     dejaba los iconos del timeline por encima de ellos. El estado visual final es
     idéntico, pero sin contexto de apilamiento residual. */
  animation: rsvFadeUp 0.5s cubic-bezier(0.22, 0.8, 0.32, 1) backwards;
  animation-delay: calc(var(--d, 0) * 70ms);
}

@media (prefers-reduced-motion: reduce) {
  .rsv-entrance .rsv-anim { animation: none; }
  .rsv-card, .rsv-btn { transition: none !important; }
}

/* ── Botonera de acciones (check-in y accesos) ── */
.rsv-actions { align-items: stretch; }

/* Barra de acciones dentro del header card (como el diseño) */
.rsv-actions-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--rsv-line-2);
}

/* Empuja las acciones terciarias (Copiar link) a la derecha, como el diseño */
.rsv-actions-bar__spacer {
  flex: 1 1 0%;
  min-width: 0;
}

.page-reservation .rsv-btn.p-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: auto;
  padding: 9px 14px;
  border-radius: 11px;
  border: 1px solid transparent;
  font-family: "DM Sans", sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: -0.01em;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  transition: transform 0.18s cubic-bezier(0.22, 0.8, 0.32, 1),
    box-shadow 0.18s ease, background 0.16s ease, border-color 0.16s ease,
    color 0.16s ease;
}

.page-reservation .rsv-btn.p-button i,
.page-reservation .rsv-btn.p-button svg { font-size: 0.82rem; flex-shrink: 0; }

.page-reservation .rsv-btn.p-button strong { font-weight: 600; }

.page-reservation .rsv-btn.p-button:enabled:hover {
  transform: translateY(-1.5px);
  box-shadow: 0 6px 16px -4px rgba(16, 24, 40, 0.16);
}
.page-reservation .rsv-btn.p-button:enabled:active { transform: translateY(0); }
.page-reservation .rsv-btn.p-button:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }

/* Primario — morado de marca, sólido y consistente con el resto */
.page-reservation .rsv-btn--primary.p-button {
  padding: 9px 16px;
  background: var(--rsv-purple, #673de6) !important;
  border-color: var(--rsv-purple, #673de6) !important;
  color: #ffffff !important;
  box-shadow: 0 1px 2px rgba(103, 61, 230, 0.3);
}
.page-reservation .rsv-btn--primary.p-button:enabled:hover {
  background: var(--rsv-purple-700, #5028c7) !important;
  border-color: var(--rsv-purple-700, #5028c7) !important;
  box-shadow: 0 6px 16px -4px rgba(103, 61, 230, 0.4);
}

/* Secundarios — tonos suaves de marca */
.page-reservation .rsv-btn--purple.p-button {
  background: var(--rsv-purple-50) !important;
  border-color: var(--rsv-purple-100) !important;
  color: var(--rsv-purple-700) !important;
}
.page-reservation .rsv-btn--purple.p-button:enabled:hover {
  background: #e7dcfc !important;
  box-shadow: 0 4px 12px rgba(103, 61, 230, 0.14);
}

.page-reservation .rsv-btn--blue.p-button {
  background: var(--rsv-blue-50) !important;
  border-color: var(--rsv-blue-100) !important;
  color: var(--rsv-blue-700) !important;
}
.page-reservation .rsv-btn--blue.p-button:enabled:hover {
  background: #e2e7fb !important;
  box-shadow: 0 4px 12px rgba(94, 114, 228, 0.14);
}

.page-reservation .rsv-btn--green-soft.p-button {
  background: var(--rsv-green-50) !important;
  border-color: var(--rsv-green-100) !important;
  color: var(--rsv-green-700) !important;
}
.page-reservation .rsv-btn--green-soft.p-button:enabled:hover {
  background: #d8f2e2 !important;
  box-shadow: 0 4px 12px rgba(17, 177, 77, 0.14);
}

/* Neutro — acción ya realizada (reenviar) */
.page-reservation .rsv-btn--neutral.p-button {
  background: var(--rsv-line-2) !important;
  border-color: var(--rsv-line) !important;
  color: var(--rsv-ink-2) !important;
}
.page-reservation .rsv-btn--neutral.p-button:enabled:hover {
  background: #eaecf1 !important;
}

/* Ghost — acciones terciarias (copiar link, editar). Mismo tamaño que los demás. */
.page-reservation .rsv-btn--ghost.p-button {
  padding: 9px 14px;
  gap: 7px;
  font-size: 13px;
  background: #ffffff !important;
  border-color: var(--rsv-line) !important;
  color: var(--rsv-ink-2) !important;
}
.page-reservation .rsv-btn--ghost.p-button:enabled:hover {
  border-color: var(--rsv-blue-100) !important;
  color: var(--rsv-blue-700) !important;
  background: var(--rsv-blue-50) !important;
}

/* ══════════════════════════════════════════════════════════════════
   Timeline — Recorrido del huésped
   ══════════════════════════════════════════════════════════════════ */
.rsv-timeline__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.rsv-timeline__heading {
  display: flex;
  align-items: center;
  gap: 12px;
}
.rsv-timeline__icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: linear-gradient(135deg, var(--rsv-green-50), var(--rsv-blue-50));
  color: var(--rsv-ink);
  display: grid;
  place-items: center;
  border: 1px solid var(--rsv-line);
  flex-shrink: 0;
}
.rsv-timeline__titlerow {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rsv-timeline__title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: var(--rsv-ink);
}
.rsv-timeline__count {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  background: var(--rsv-green-50);
  color: var(--rsv-green-700);
  border: 1px solid var(--rsv-green-100);
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}
.rsv-timeline__subtitle {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: var(--rsv-ink-3);
}
.rsv-timeline__filters {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--rsv-line-2);
  padding: 4px;
  border-radius: 11px;
  border: 1px solid var(--rsv-line);
  flex-wrap: wrap;
}
.rsv-timeline__filter {
  padding: 6px 12px;
  border-radius: 8px;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: var(--rsv-ink-3);
  font-family: "DM Sans", sans-serif;
  transition: background 0.24s cubic-bezier(0.22, 0.8, 0.32, 1),
    color 0.2s ease, box-shadow 0.24s cubic-bezier(0.22, 0.8, 0.32, 1),
    transform 0.16s ease;
}
.rsv-timeline__filter:not(.is-active):hover {
  color: var(--rsv-ink);
}
.rsv-timeline__filter:active {
  transform: scale(0.97);
}
.rsv-timeline__filter.is-active {
  background: #fff;
  color: var(--rsv-ink);
  font-weight: 700;
  box-shadow: 0 1px 7px rgba(15, 23, 42, 0.12);
}

/* Animación profesional al cambiar de filtro del timeline (fade-up out-in) */
.rsv-tl-enter-active {
  transition: opacity 0.32s ease, transform 0.32s cubic-bezier(0.22, 0.8, 0.32, 1);
}
.rsv-tl-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.rsv-tl-enter {
  opacity: 0;
  transform: translateY(8px);
}
.rsv-tl-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Barra de progreso */
.rsv-timeline__list { display: flex; flex-direction: column; }

/* Cabecera de día — alineada sobre la columna de contenido */
.rsv-tl-day {
  display: flex;
  align-items: center;
  gap: 10px;
  /* alinea con la columna de contenido: 60px gutter + 14 + 34px rail + 14 */
  padding-left: 122px;
  margin-bottom: 14px;
}

.rsv-tl-day__chip {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--rsv-ink-3);
  background: var(--rsv-line-2);
  border: 1px solid var(--rsv-line);
  padding: 4px 11px;
  border-radius: 999px;
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
}

.rsv-tl-day__rule {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, var(--rsv-line), transparent);
}

/* Entrada */
/* La historia se escribe sola al abrir: cada evento entra justo después del
   anterior. 45 ms de separación — por debajo no se nota, por encima se espera.
   Solo opacity y transform, que no tocan layout y van por GPU. */
@keyframes rsvTlIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: none; }
}

.rsv-tl-entry {
  display: grid;
  grid-template-columns: 60px 34px 1fr;
  column-gap: 14px;
  animation: rsvTlIn 340ms cubic-bezier(0.23, 1, 0.32, 1) both;
  animation-delay: calc(var(--i, 0) * 45ms);
}

.rsv-tl-day {
  animation: rsvTlIn 340ms cubic-bezier(0.23, 1, 0.32, 1) both;
}

/* Quien pide menos movimiento ve lo mismo, sin desplazamiento. */
@media (prefers-reduced-motion: reduce) {
  .rsv-tl-entry,
  .rsv-tl-day {
    animation-duration: 160ms;
    animation-delay: 0ms;
    animation-name: rsvTlFade;
  }

  @keyframes rsvTlFade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
}

.rsv-tl-entry__time {
  text-align: right;
  padding-top: 12px;
}

.rsv-tl-entry__hour {
  font-size: 12px;
  color: var(--rsv-ink-2);
  font-weight: 600;
  letter-spacing: -0.01em;
}

.rsv-tl-entry__rail {
  position: relative;
  display: flex;
  justify-content: center;
}

.rsv-tl-entry__line {
  position: absolute;
  top: 40px;
  bottom: -14px;
  width: 2px;
  background: var(--rsv-line);
  border-radius: 2px;
}

.rsv-tl-entry__dot {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #fff;
  display: grid;
  place-items: center;
  margin-top: 6px;
  /* position:relative (SIN z-index) mantiene el punto por encima de la línea vertical
     por orden de pintado, sin crear un stacking context que se cuele sobre los modales. */
  position: relative;
  box-shadow: 0 0 0 4px #fff, 0 2px 4px rgba(16, 24, 40, 0.06);
  border: 1.5px solid;
  font-size: 13px;
}

.rsv-tl-entry__dot--green   { border-color: var(--rsv-green);  color: var(--rsv-green-700); }
.rsv-tl-entry__dot--blue    { border-color: var(--rsv-blue);   color: var(--rsv-blue-700); }
.rsv-tl-entry__dot--purple  { border-color: var(--rsv-purple); color: var(--rsv-purple-700); }
.rsv-tl-entry__dot--neutral { border-color: #d3d7e1; color: var(--rsv-ink-4); border-style: dashed; }

.rsv-tl-entry__card {
  position: relative;
  background: #fff;
  border: 1px solid var(--rsv-line);
  border-radius: 14px;
  padding: 12px 16px 12px 18px;
  margin-bottom: 16px;
  box-shadow: var(--rsv-shadow-xs);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.rsv-tl-entry__card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 2px;
  border-radius: 2px;
  opacity: 0.7;
}

.rsv-tl-entry__card--green::before  { background: var(--rsv-green); }
.rsv-tl-entry__card--blue::before   { background: var(--rsv-blue); }
.rsv-tl-entry__card--purple::before { background: var(--rsv-purple); }
.rsv-tl-entry__card--neutral::before { background: #d3d7e1; }

.rsv-tl-entry__card:hover {
  border-color: #e0e2ea;
  box-shadow: var(--rsv-shadow-sm);
}


.rsv-tl-entry__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.rsv-tl-entry__title {
  font-size: 14px;
  color: var(--rsv-ink);
  letter-spacing: -0.15px;
  line-height: 1.35;
  min-width: 0;
}
.rsv-tl-entry__who {
  background: linear-gradient(transparent 60%, var(--rsv-green-100) 60%);
  padding: 0 3px;
  font-weight: 700;
  color: var(--rsv-ink);
}
.rsv-tl-entry__action {
  font-weight: 600;
  color: var(--rsv-ink-2);
  margin-left: 3px;
}

/* Eventos pendientes (aún no realizados): gris más claro para distinguirlos. */

.rsv-tl-entry__tag {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.3;
}

.rsv-tl-entry__tag--green   { color: var(--rsv-green-700);  background: var(--rsv-green-50);  border: 1px solid var(--rsv-green-100); }
.rsv-tl-entry__tag--blue    { color: var(--rsv-blue-700);   background: var(--rsv-blue-50);   border: 1px solid var(--rsv-blue-100); }
.rsv-tl-entry__tag--purple  { color: var(--rsv-purple-700); background: var(--rsv-purple-50); border: 1px solid var(--rsv-purple-100); }
.rsv-tl-entry__tag--neutral { color: var(--rsv-ink-3);      background: var(--rsv-line-2);    border: 1px solid var(--rsv-line); }

.rsv-tl-entry__desc {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: var(--rsv-ink-3);
  line-height: 1.5;
}

/* Diff de una modificación (viejo → nuevo) */
.rsv-tl-changes {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.rsv-tl-change {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
}
.rsv-tl-change__label {
  font-weight: 700;
  color: var(--rsv-ink-2, #4b5563);
  min-width: 78px;
}
.rsv-tl-change__old {
  color: #9aa3b2;
  text-decoration: line-through;
}
.rsv-tl-change__arrow { color: #aab2c0; }
.rsv-tl-change__new {
  font-weight: 700;
  color: var(--rsv-green-700, #0a8a3a);
}

/* Evento clicable (p. ej. WhatsApp → llevar al chat) */
.rsv-tl-entry__card--clickable { cursor: pointer; }
.rsv-tl-entry__card--clickable:hover {
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}
.rsv-tl-entry__chatlink {
  display: inline-block;
  margin-top: 6px;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--rsv-green-700, #0a8a3a);
}

/* ── Responsive ── */
/* El panel derecho ocupa ~49% del ancho (base). Por debajo de 1100px se apila. */
@media (max-width: 1100px) {
  .rsv-layout { grid-template-columns: 1fr; }
  .rsv-side {
    position: static;
    height: auto;
    min-height: 540px;
    margin-bottom: 20px;
  }
}

@media (max-width: 640px) {
  .rsv-row { grid-template-columns: 1fr; }
}
</style>

<style lang="scss">
/* Códigos caducados: constancia de que se enviaron, claramente secundarios
   frente al código vigente (tachados y en tono apagado). */
.pin-expired {
  padding: 0.6rem 0.7rem;
  background: var(--rsv-line-2, #f7f8fb);
  border-radius: 10px;

  .pin-label {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--rsv-ink-3, #6b7388);
    font-family: "DM Sans", sans-serif;
    font-size: 0.78rem;
    font-weight: 700;
  }

  &__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.3rem;
  }

  &__code {
    padding: 0.08rem 0.4rem;
    background: #fff;
    border-radius: 6px;
    color: var(--rsv-ink-3, #6b7388);
    font-weight: 600;
    text-decoration: line-through;
  }

  &__meta {
    color: var(--rsv-ink-4, #9aa2b5);
    font-family: "DM Sans", sans-serif;
    font-size: 0.75rem;
  }
}

.pin-display {
  padding: 0.6rem 0.7rem;
  background: var(--rsv-line-2, #f7f8fb);
  border: 1px solid var(--rsv-line, #ecedf2);
  border-radius: 10px;

  .pin-label {
    display: block;
    margin-bottom: 0.3rem;
    color: var(--rsv-ink-3, #6b7388);
    font-family: "DM Sans", sans-serif;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  code {
    background: white;
    padding: 0.4rem 0.8rem;
    border-radius: 7px;
    display: inline-block;
    font-family: "DM Mono", "DM Sans", monospace;
    font-size: 1rem;
    font-weight: 700;
    color: var(--rsv-blue, #5e71e4);
    border: 1px solid var(--rsv-blue-100, #dbe4ff);
    letter-spacing: 0.08em;
  }
}

.pin-display--ttlock .ttlock-pin-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.4rem;
}

.ttlock-pin-entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.45rem 0.6rem;
  background: #ffffff;
  border: 1px solid var(--rsv-line, #ecedf2);
  border-radius: 8px;
}

.ttlock-pin-entry__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ttlock-pin-entry__meta small {
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.2;
}

.ttlock-pin-entry__label {
  color: #1e293b;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
}

/* Chip de validez en el header de "Códigos y accesos" */
.rsv-valid-chip {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  background: var(--rsv-green-50, #e7f7ee);
  border: 1px solid var(--rsv-green-100, #cdefd9);
  color: var(--rsv-green-700, #0a8a3a);
  font-family: "DM Sans", sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1;
}
.rsv-valid-chip svg { flex-shrink: 0; }
.rsv-valid-chip b { font-weight: 700; }
.rsv-valid-chip small { opacity: 0.7; font-size: 10.5px; }

/* ===== Acceso digital del huésped (Shelly) — compacto, en grid ===== */
.access-block {
  margin-top: 0.5rem;
}

.access-block__label {
  display: block;
  margin-bottom: 0.45rem;
  color: var(--rsv-ink-3, #6b7388);
  font-family: "DM Sans", sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

/* Nota de acceso gestionado por YACAN */
.yacan-access-note {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.7rem 0.85rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.82rem;
  color: #475569;
  line-height: 1.45;
}
.yacan-access-note i {
  color: #5e71e4;
  margin-top: 2px;
}

/* Llaves en fila: tantas columnas como quepan (mín. 260px), no apiladas */
.access-link-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.45rem;
}

/* Fila compacta: [icono] label …… [Copiar][Abrir] */
.access-link-entry {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.4rem 0.55rem;
  background: #ffffff;
  border: 1px solid var(--rsv-line, #ecedf2);
  border-radius: 9px;
}

.access-link-entry__icon {
  width: 26px;
  height: 26px;
  min-width: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: var(--rsv-green-50, #e7f7ee);
  color: var(--rsv-green, #11b14d);
  font-size: 0.72rem;
}

.access-link-entry__label {
  flex: 1;
  min-width: 0;
  color: var(--rsv-ink, #1e293b);
  font-family: "DM Sans", sans-serif;
  font-size: 0.84rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.access-link-entry__actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.access-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  padding: 0.3rem 0.6rem;
  border-radius: 7px;
  border: 1px solid var(--rsv-line, #e2e8f0);
  background: #ffffff;
  color: var(--rsv-ink-2, #475569);
  font-family: "DM Sans", sans-serif;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.access-link-btn i {
  font-size: 0.68rem;
}

.access-link-btn:hover {
  border-color: #5e71e4;
  color: #5e71e4;
  background: #f1f3ff;
  text-decoration: none;
}

/* Estados completos para que los estilos globales de <a> no pisen el blanco */
.access-link-btn--primary,
.access-link-btn--primary:link,
.access-link-btn--primary:visited {
  background: #5e71e4;
  border-color: #5e71e4;
  color: #ffffff !important;
  text-decoration: none;
}

.access-link-btn--primary:hover,
.access-link-btn--primary:focus,
.access-link-btn--primary:active {
  background: #4a5dc9;
  border-color: #4a5dc9;
  color: #ffffff !important;
  text-decoration: none;
}
</style>

<style lang="scss" scoped>
.request-status-banner,
.rejected-status-banner,
.intent-status-banner,
.expired-status-banner,
.overbooking-status-banner,
.page-reservation,
.box-title,
.reservation-meta-note,
:deep(.p-button),
:deep(.p-dialog),
:deep(.p-confirm-dialog),
:deep(.p-menu) {
  font-family: "DM Sans", sans-serif;
}

.rsb-content strong,
.rsb-content--rejected strong,
.isb-content strong,
.esb-content strong,
.osb-content strong,
.rsb-btn,
.isb-paid-btn,
.isb-expire-btn,
.osb-confirm-btn,
.page-reservation .box-title,
:deep(.p-button strong) {
  font-family: "DM Sans", sans-serif;
}

.rsb-content span,
.rsb-content--rejected span,
.isb-content span,
.esb-content span,
.isb-countdown small,
.reservation-meta-note small {
  font-family: "DM Sans", sans-serif;
}

/* ── Banners de estado: card blanco + acento lateral táctico ── */
.request-status-banner,
.rejected-status-banner,
.intent-status-banner,
.expired-status-banner,
.overbooking-status-banner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 14px 21px;
  background: #ffffff;
  border: 1px solid var(--rsv-line, #ecedf2);
  border-radius: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05), 0 1px 2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
  animation: rsvFadeUp 0.45s cubic-bezier(0.22, 0.8, 0.32, 1) both;
}

.request-status-banner::before,
.rejected-status-banner::before,
.intent-status-banner::before,
.expired-status-banner::before,
.overbooking-status-banner::before {
  content: "";
  position: absolute;
  left: 0;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 3px;
}

.request-status-banner::before { background: #673de6; }
.rejected-status-banner::before { background: #dc2626; }
.intent-status-banner::before { background: #f59e0b; }
.expired-status-banner::before { background: #9aa2b5; }
.overbooking-status-banner::before { background: #e11d48; }

.osb-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #be123c;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.osb-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 220px;
}

.osb-content strong {
  color: var(--rsv-ink, #0e1320);
  font-size: 13.8px;
  letter-spacing: -0.01em;
}

.osb-content span {
  color: var(--rsv-ink-3, #6b7388);
  font-size: 12.5px;
  line-height: 1.5;
}

/* Aviso puesto a mano en una reserva (src/config/avisosPorReserva.js): rojo
   lleno y texto blanco, para que no pase desapercibido. */
.aviso-manual-banner {
  background: #e11d48;
  border-color: #e11d48;
  box-shadow: 0px 0px 20px 3px rgba(225, 29, 72, 0.18);
}
.aviso-manual-banner::before { display: none; }
.aviso-manual-banner .osb-icon {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.35);
  color: #ffffff;
}
.aviso-manual-banner .osb-content strong,
.aviso-manual-banner .osb-content span {
  color: #ffffff;
}

.osb-actions {
  display: flex;
  align-items: center;
}

.osb-confirm-btn {
  background: #e11d48;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.osb-confirm-btn:hover:not(:disabled) {
  background: #be123c;
}

.osb-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rsb-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: #f0eafd;
  border: 1px solid #e1d6fb;
  color: #5028c7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rsb-content {
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  strong {
    font-size: 13.8px;
    color: var(--rsv-ink, #0e1320);
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  span {
    font-size: 12.5px;
    color: var(--rsv-ink-3, #6b7388);
    font-weight: 500;
    line-height: 1.5;
  }
}

.rsb-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}




.rsb-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 34px;
  padding: 0 13px;
  border-radius: 10px;
  border: 1.5px solid transparent;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--approve {
    background: #dcfce7;
    color: #15803d;
    border-color: #86efac;

    &:hover:not(:disabled) {
      background: #bbf7d0;
      transform: translateY(-1px);
    }
  }

  &--reject {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;

    &:hover:not(:disabled) {
      background: #fee2e2;
      transform: translateY(-1px);
    }
  }
}

.rsb-icon--rejected {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}

.rsb-content--rejected {
  strong {
    color: var(--rsv-ink, #0e1320);
  }

  span {
    color: var(--rsv-ink-3, #6b7388);
  }
}

.isb-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #b45309;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.isb-content {
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  strong {
    font-size: 13.8px;
    color: var(--rsv-ink, #0e1320);
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  span {
    font-size: 12.5px;
    color: var(--rsv-ink-3, #6b7388);
    font-weight: 500;
    line-height: 1.5;
  }
}

.isb-side {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.isb-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.isb-countdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  .isb-time {
    font-family: "DM Sans", sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: #92400e;
    letter-spacing: -0.04em;
  }

  small {
    font-size: 10px;
    color: #a16207;
    font-weight: 700;
    text-transform: uppercase;
  }
}

.isb-paid-btn,
.isb-expire-btn {
  height: 35px;
  padding: 0 13px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.15s ease;
}

.isb-paid-btn {
  border: 1px solid #86efac;
  background: #dcfce7;
  color: #166534;

  &:hover:not(:disabled) {
    background: #bbf7d0;
    transform: translateY(-1px);
  }
}

.isb-paid-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.isb-expire-btn {
  border: 1px solid #f5c2c7;
  background: #fff1f2;
  color: #b42318;

  &:hover:not(:disabled) {
    background: #ffe4e8;
    transform: translateY(-1px);
  }
}

.isb-expire-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Banner de AMPLIACIÓN pendiente (misma reserva) ── */
.ext-banner {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 16px;
  border-radius: 14px;
  background: #f4fbf6;
  border: 1px solid #cdeed7;
  margin: 0 0 14px;
}

/* Observación de la reserva (informativa) */
.obs-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 16px;
  border-radius: 14px;
  background: #fff8ed;
  border: 1px solid #fbe6c4;
  margin: 0 0 14px;
}
.obs-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fdeecf;
  color: #b9770a;
}
.obs-content { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.obs-content strong {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b9770a;
}
.obs-content span {
  font-size: 13.5px;
  color: #4b5563;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.obs-banner--extras { background: #f0fdf4; border-color: #bbf7d0; }
.obs-banner--extras .obs-icon { background: #dcfce7; color: #047857; }
.obs-banner--extras .obs-content strong { color: #047857; }
.ext-banner--requested { background: #fffdf5; border-color: #fde9b0; }
.ext-banner--approved { background: #f0fdf4; border-color: #bbf7d0; }
.ext-banner--approved .exb-icon { background: #dcfce7; border-color: #bbf7d0; color: #16a34a; }
.ext-banner--rejected { background: #fef2f2; border-color: #fecaca; }
.ext-banner--rejected .exb-icon { background: #fee2e2; border-color: #fecaca; color: #b91c1c; }

/* Modal de aprobación de solicitud "a consultar" */
.sr-modal-overlay { position: fixed; inset: 0; z-index: 100060; background: rgba(17,24,39,.5); display: flex; align-items: center; justify-content: center; padding: 20px; }
.sr-modal { width: 100%; max-width: 400px; background: #fff; border-radius: 16px; padding: 24px; box-shadow: 0 20px 60px rgba(16,24,40,.28); font-family: "DM Sans", sans-serif; }
.sr-modal__title { font-size: 18px; font-weight: 800; color: #111827; margin: 0 0 2px; }
.sr-modal__svc { font-size: 13.5px; color: #6b7280; margin: 0 0 18px; }
.sr-modal__label { display: block; font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 8px; }
.sr-modal__opt { color: #9aa2b5; font-weight: 500; font-size: 11px; }
.sr-modal__amount { display: flex; align-items: center; border: 1.5px solid #e2e6ee; border-radius: 12px; overflow: hidden; height: 48px; }
.sr-modal__amount:focus-within { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.14); }
.sr-modal__amount input { flex: 1; min-width: 0; border: 0; outline: none; padding: 0 14px; font: inherit; font-size: 18px; font-weight: 800; color: #111827; }
.sr-modal__cur { padding: 0 14px; font-size: 13px; font-weight: 800; color: #6b7280; background: #f6f7fa; align-self: stretch; display: flex; align-items: center; }
.sr-modal__hint { font-size: 12px; line-height: 1.5; color: #6b7280; margin: 12px 0 0; }
.sr-modal__actions { display: flex; gap: 10px; margin-top: 20px; }
.sr-modal__cancel { flex: 1; height: 44px; border: 1.5px solid #e2e6ee; background: #fff; border-radius: 11px; font: inherit; font-weight: 700; font-size: 14px; color: #374151; cursor: pointer; }
.sr-modal__cancel:hover:not(:disabled) { background: #f8fafc; }
.sr-modal__ok { flex: 1.4; height: 44px; border: 0; background: #6366f1; border-radius: 11px; font: inherit; font-weight: 700; font-size: 14px; color: #fff; cursor: pointer; transition: transform .12s ease, background .15s ease; }
.sr-modal__ok:hover:not(:disabled) { background: #5457e5; }
.sr-modal__ok:active:not(:disabled) { transform: scale(0.98); }
.sr-modal__ok:disabled, .sr-modal__cancel:disabled { opacity: .55; cursor: default; }

/* Aparición/desaparición suave del banner (creación en vivo y al confirmarse el pago) */
.exb-enter-active, .exb-leave-active {
  transition: opacity .4s ease, transform .4s cubic-bezier(.22,.61,.36,1), max-height .45s ease, margin .4s ease, padding .4s ease;
  overflow: hidden;
  max-height: 220px;
}
.exb-enter, .exb-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  max-height: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.exb-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: #e7f7ec;
  border: 1px solid #c2e9cf;
  color: #15803d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ext-banner--requested .exb-icon { background: #fff7e6; border-color: #fce3a6; color: #b45309; }

.exb-content {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.exb-content strong {
  font-size: 13.8px;
  color: #0e1320;
  font-weight: 700;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.exb-plazo { font-variant-numeric: tabular-nums; }
.exb-plazo--poco { color: #b42318; }
.exb-code {
  font-size: 11px;
  font-weight: 700;
  color: #15803d;
  background: #e7f7ec;
  border-radius: 6px;
  padding: 1px 7px;
  letter-spacing: 0.02em;
}
.ext-banner--requested .exb-code { color: #b45309; background: #fff7e6; }
.exb-content span { font-size: 12.5px; color: #6b7388; font-weight: 500; line-height: 1.5; }
.exb-content b { color: #0e1320; font-weight: 700; }

.exb-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.exb-paid-btn,
.exb-reject-btn {
  height: 35px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.15s ease;
}
.exb-paid-btn { border: 1px solid #86efac; background: #dcfce7; color: #166534; }
.exb-paid-btn:hover:not(:disabled) { background: #bbf7d0; transform: translateY(-1px); }
.exb-reject-btn { border: 1px solid #f5c2c7; background: #fff1f2; color: #b42318; }
.exb-reject-btn:hover:not(:disabled) { background: #ffe4e8; transform: translateY(-1px); }
.exb-paid-btn:disabled,
.exb-reject-btn:disabled { opacity: 0.45; cursor: not-allowed; }

@media (max-width: 640px) {
  .ext-banner { flex-wrap: wrap; }
  .exb-actions { width: 100%; }
  .exb-paid-btn, .exb-reject-btn { flex: 1; }
}

.esb-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: #f3f4f8;
  border: 1px solid #e5e7eb;
  color: #6b7388;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.esb-content {
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  strong {
    font-size: 13.8px;
    color: var(--rsv-ink, #0e1320);
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  span {
    font-size: 12.5px;
    color: var(--rsv-ink-3, #6b7388);
    font-weight: 500;
    line-height: 1.5;
  }
}
</style>

<style lang="scss">
#dropdown-1__BV_toggle_ {
  background: #4caf5029;
  color: #019e35;
}

/* El despeje superior móvil lo gestiona .contenedor-main--reservation (Maqueta). */

.page-reservation {
  font-family: "DM Sans", sans-serif;

  /* El fondo gris y el padding viven en .contenedor-main--reservation
     (ver Maqueta/index.vue): esta vista queda transparente y sin caja. */

  /* Los textos secundarios, en la misma familia que el resto */
  .rsv-card__sub,
  .rsv-tl-entry__desc,
  .rsv-tl-entry__time,
  small {
    font-family: "DM Sans", sans-serif;
  }

  .disabled-pe {
    pointer-events: none;
    opacity: 0.5;
  }

  .p-dialog {
    max-width: 563px !important;
  }

  .p-dialog-content {
    gap: 2rem !important;
  }

  .input-style {
    height: 33px;
    border-radius: 8px;
    padding-inline: 12px;
    justify-content: space-between;
    align-items: center;
    background: rgb(214 214 214 / 22%);
    width: 100%;

    input {
      height: 100%;
      border: none;
      border-radius: 24px;
      width: 90%;
      font-family: "DM Sans", sans-serif;
      font-weight: 600;
      color: #5e71e4;
      background: transparent;
    }
  }

  .icon-svg {
    width: 35px;
    height: 33px;
    background: #ffffff;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 30px 2px #eee;
  }

  .box-title {
    display: inline-block;
    color: #64748b;
    font-family: "DM Sans", sans-serif;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin-bottom: 0.15rem;
  }

  .reservation-meta-note {
    small {
      color: #64748b;
      font-family: "DM Sans", sans-serif;
      font-size: 0.82rem;
      font-weight: 700;
      line-height: 1.45;
    }
  }

  .p-button {
    text-align: left;
    border-radius: 10px;
    font-family: "DM Sans", sans-serif;
    font-weight: 700;
    letter-spacing: -0.01em;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
  }

  .p-menu .p-menuitem {
    font-family: "DM Sans", sans-serif;
    font-weight: 600;
  }

  .p-button-danger {
    background-color: #c62828;
    border-color: #c62828;
  }
}
</style>

<style lang="scss">
/* ══════════════════════════════════════════════════════════════════
   Modal — Código de acceso estático
   Fuente: DM Sans
   Colores marca: #5e71e4 (primary) · #059669 (acceso/verde)
   ══════════════════════════════════════════════════════════════════ */

/* Transición del overlay */
.scm-fade-enter-active,
.scm-fade-leave-active {
  transition: opacity 0.2s ease;
  .scm-card { transition: transform 0.22s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.2s ease; }
}
.scm-fade-enter,
.scm-fade-leave-to {
  opacity: 0;
  .scm-card { transform: translateY(12px) scale(0.98); opacity: 0; }
}

/* Overlay de fondo */
.scm-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* Tarjeta del modal */
.scm-card {
  font-family: 'DM Sans', sans-serif;
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 460px;
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.06),
    0 20px 60px rgba(15, 23, 42, 0.14),
    0 4px 12px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

/* ── Header ─────────────────────────────────────────────────── */
.scm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 24px 20px;
  border-bottom: 1px solid #f1f3f5;
}

.scm-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.scm-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: #edfaf4;
  border: 1px solid #c6f0de;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  font-size: 16px;
  flex-shrink: 0;
}

.scm-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
}

.scm-subtitle {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 2px 0 0;
}

/* Botón cerrar — caja con hover */
.scm-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #111827;
  }

  &:active {
    background: #e5e7eb;
  }
}

/* ── Body ────────────────────────────────────────────────────── */
.scm-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.scm-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scm-section-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.scm-section-note {
  font-size: 0.75rem;
  font-weight: 400;
  color: #9ca3af;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 6px;
}

/* Badge del código actual */
.scm-code-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #f0fdf8;
  border: 1.5px solid #a7f3d0;
  border-radius: 12px;
  padding: 11px 18px;
  width: fit-content;

  i {
    color: #059669;
    font-size: 14px;
  }

  span {
    font-family: 'Courier New', 'SF Mono', monospace;
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: 4px;
    color: #064e3b;
  }
}

/* Input del nuevo código */
.scm-input {
  width: 100%;
  font-family: 'Courier New', 'SF Mono', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 13px 16px;
  color: #5e71e4;
  background: #ffffff;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;

  &::placeholder {
    color: #d1d5db;
    letter-spacing: 2px;
    font-weight: 400;
  }

  &:focus {
    border-color: #5e71e4;
    box-shadow: 0 0 0 3px rgba(94, 113, 228, 0.12);
  }
}

.scm-hint-text {
  font-size: 0.76rem;
  color: #94a3b8;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  i {
    margin-top: 1px;
    color: #94a3b8;
    flex-shrink: 0;
    font-size: 11px;
  }
}

/* ── Footer ──────────────────────────────────────────────────── */
.scm-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 22px;
  border-top: 1px solid #f1f3f5;
}

.scm-btn {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 10px;
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: background 0.15s, box-shadow 0.15s, opacity 0.15s;
  line-height: 1;

  &--cancel {
    background: #ffffff;
    color: #374151;
    border: 1.5px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
      border-color: #d1d5db;
    }

    &:active {
      background: #f3f4f6;
    }
  }

  &--primary {
    background: #5e71e4;
    color: #ffffff;
    border: 1.5px solid transparent;

    &:hover:not(:disabled) {
      background: #4a5fd4;
      box-shadow: 0 4px 14px rgba(94, 113, 228, 0.35);
    }

    &:active:not(:disabled) {
      background: #3a4ec4;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
